import React, {
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { WandSparkles, CircleGauge, GraduationCap, Loader2, MessageCircle, Quote, PlayCircle, ClipboardCopy, Check, Sparkles, Lightbulb, Languages } from "lucide-react";
import { FloatingToolbar } from "@/components/FloatingToolbar";
import { useUser } from "@/lib/context/user-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePlayer } from "@/contexts/player-context";
// motion and AnimatePresence removed to reduce CPU consumption from subtitle animations
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserSettings } from "@/contexts/user-settings-context";
import { vocabularyLevelChecker } from "@/lib/vocabularyLevel";
import { resolveDictionaryDisplayName } from "@/lib/vocabulary/dictionary-display";

// Import optimized hooks and components
import { useWordExplanation } from "@/hooks/use-word-explanation";
import { useUIState } from "@/hooks/use-ui-state";
import { useOptimizedTextRenderer } from "@/hooks/use-optimized-text-renderer";
import {
  vocabularyErrorHandler,
} from "@/lib/vocabulary-error-handler";
import { VocabCard } from "@/components/vocabulary/VocabCard";
// import { AnalysisDialog } from "@/components/vocabulary/AnalysisDialog";
import { StructuredAnalysisDialog } from "@/components/vocabulary/StructuredAnalysisDialog";
import { MobileAnalysisDrawer } from "@/components/vocabulary/MobileAnalysisDrawer";
import { formatTime, getSpeakerLang } from "@/utils/device-detection";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { WordHoverCard } from "./vocabulary/WordHoverCard";
import { useYoudaoTranslation } from "@/hooks/use-youdao-translation";
// 移除 useObject 导入，改用手动 fetch 实现，避免自动轮询请求
// import { experimental_useObject as useObject } from '@ai-sdk/react';
// import { analysisSchema } from '@/app/api/analyze-sentence/schema';
import { calculateOptimalSimilarity } from "@/lib/text-similarity";
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { TextDiffHighlight } from "@/lib/text-diff-highlight-improved";
import { useSaveShadowingRecord } from "@/hooks/useShadowingStats";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { mediaCueRegistry } from "@/lib/media-cue-registry";

const STATUS_OPTION_CONFIG = [
  {
    value: 'NEW',
    labelKey: 'vocabulary.status.new',
    defaultLabel: 'New',
    descriptionKey: 'vocabulary.status.newDescription',
    defaultDescription: 'Just added, needs first review',
  },
  {
    value: 'FUZZY',
    labelKey: 'vocabulary.status.fuzzy',
    defaultLabel: 'Fuzzy',
    descriptionKey: 'vocabulary.status.fuzzyDescription',
    defaultDescription: 'Needs more practice soon',
  },
  {
    value: 'MASTERED',
    labelKey: 'vocabulary.status.mastered',
    defaultLabel: 'Mastered',
    descriptionKey: 'vocabulary.status.masteredDescription',
    defaultDescription: 'Confident and on long review',
  },
] as const;

type StatusValue = typeof STATUS_OPTION_CONFIG[number]['value'];

type StatusOption = {
  value: StatusValue;
  label: string;
  description: string;
};

const statusStyles: Record<StatusValue, string> = {
  NEW: 'bg-sky-100 text-sky-700 border-sky-200',
  FUZZY: 'bg-amber-100 text-amber-700 border-amber-200',
  MASTERED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const statusAccentMap: Record<StatusValue, string> = {
  NEW: 'bg-sky-400',
  FUZZY: 'bg-amber-400',
  MASTERED: 'bg-emerald-400',
};

const PLAYBACK_LOCK_KEY = 'grouped-transcript-sync';

// ===== 事件驱动系统核心定义 =====
interface ChunkState {
  id: string;
  index: number;
  status: 'pending' | 'active' | 'practicing' | 'completed' | 'skipped';
  practiceData: {
    incompleteWords: string[];
    completedWords: string[];
    completionPercentage: number;
    attempts: number;
  };
  timestamps: {
    firstVisit: number;
    lastVisit: number;
    completedAt?: number;
  };
  metadata: {
    hasVocabularyWords: boolean;
    isVisited: boolean;
    shouldTriggerDialog: boolean;
  };
}

interface DialogTriggerEvent {
  type: 'vocabulary_quiz' | 'shadowing_practice' | 'chunk_completed';
  chunkId: string;
  chunkIndex: number;
  triggerReason: string;
  payload: any;
  timestamp: number;
}

// ===== 状态机核心类 =====
class ChunkStateMachine {
  // 🔥 修复内存泄漏：使用 WeakMap 替代静态 Record，避免内存累积
  // 静态属性改为限制大小的Map，防止无限增长
  private static globalUserInteractionMap = new Map<string, boolean>();
  private static globalCompletedStates = new Map<string, { completedWords: string[], shouldTriggerDialog: boolean }>();
  private static readonly MAX_CACHE_SIZE = 1000; // 限制最大缓存数量
  
  private states = new Map<string, ChunkState>();
  private eventQueue: DialogTriggerEvent[] = [];
  private listeners = new Map<string, Set<(event: DialogTriggerEvent) => void>>();
  private groupId: string; // 添加groupId属性
  private config: {
    practiceMode: boolean;
    practiceModeType: 'vocabulary' | 'toefl' | 'shadowing';
    vocabularyTerms: string[];
    isWordCheckerReady: boolean;
  };

  constructor(chunks: any[], config: any, groupId?: string) {
    this.config = config;
    this.groupId = groupId || Date.now().toString(); // 添加group标识
    // 🔥 关键修复：不要每次都清空状态，保持用户的学习进度
    // 只在真正需要重新开始时才清空
    this.initializeStates(chunks);
  }

  private initializeStates(chunks: any[]) {
    chunks.forEach((chunk, index) => {
      const chunkId = `${this.groupId}-chunk-${index}`; // 🔥 关键修复：包含groupId，确保每个group的chunk都有唯一标识
      const allWords = this.extractVocabularyWords(chunk.text);
      
      // 检查全局完成状态
      const globalState = ChunkStateMachine.globalCompletedStates.get(chunkId);
      const completedWords = globalState?.completedWords || [];
      const incompleteWords = allWords.filter(word => !completedWords.includes(word));
      const completionPercentage = allWords.length > 0 ? (completedWords.length / allWords.length) * 100 : 0;
      // 🔥 关键修复：重新计算完成状态，确保状态一致性
      const isCompleted = incompleteWords.length === 0 && allWords.length > 0;
      const hasVocabularyWords = this.config.practiceModeType === 'shadowing' ? true : allWords.length > 0;
      const defaultShouldTrigger = this.config.practiceMode
        ? (this.config.practiceModeType === 'shadowing'
            ? hasVocabularyWords
            : (hasVocabularyWords && !isCompleted))
        : false;
      const persistedShouldTrigger = typeof globalState?.shouldTriggerDialog === 'boolean'
        ? globalState.shouldTriggerDialog
        : undefined;
      const shouldTriggerDialog = typeof persistedShouldTrigger === 'boolean'
        ? persistedShouldTrigger
        : defaultShouldTrigger;
      
      this.states.set(chunkId, {
        id: chunkId,
        index,
        // ✅ Shadowing 模式下，如果之前标记为不再弹窗，则视为已完成，避免重新触发
        status: (this.config.practiceModeType === 'shadowing' && persistedShouldTrigger === false)
          ? 'completed'
          : (isCompleted ? 'completed' : 'pending'),
        practiceData: {
          incompleteWords,
          completedWords,
          completionPercentage,
          attempts: 0
        },
        timestamps: {
          firstVisit: 0,
          lastVisit: 0
        },
        metadata: {
          // Shadowing模式下，所有chunk都视为有内容需要练习
          hasVocabularyWords,
          isVisited: false,
          // ✅ 持久化shouldTriggerDialog，允许尊重用户的跳过操作
          shouldTriggerDialog
        }
      });
      
    });
  }


  // 获取所有chunk的隐藏词汇映射（用于初始化）
  getAllHiddenWordsMap(chunks: any[]): Record<string, boolean> {
    
    if (!this.config.practiceMode) {
      return {};
    }

    const hiddenWordsMap: Record<string, boolean> = {};
    
    chunks.forEach((chunk, index) => {
      const chunkId = `${this.groupId}-chunk-${index}`;
      let chunkState = this.states.get(chunkId);
      if (!chunkState) {
        // 重新创建chunk状态
        const incompleteWords = this.extractVocabularyWords(chunk.text);
        
        chunkState = {
          id: chunkId,
          index,
          status: 'pending',
          practiceData: {
            incompleteWords,
            completedWords: [],
            completionPercentage: 0,
            attempts: 0
          },
          timestamps: {
            firstVisit: Date.now(),
            lastVisit: Date.now()
          },
          metadata: {
            // Shadowing模式下，所有chunk都视为有内容需要练习
            hasVocabularyWords: this.config.practiceModeType === 'shadowing' ? true : incompleteWords.length > 0,
            isVisited: false,
            shouldTriggerDialog: this.config.practiceModeType === 'shadowing' 
              ? this.config.practiceMode
              : incompleteWords.length > 0
          }
        };
        
        this.states.set(chunkId, chunkState);
      }

      // 获取chunk中的所有词汇
      const vocabularyWords = this.getChunkVocabularyWords(index, chunk.text);
      
      // 生成多种格式的key以确保兼容性
      vocabularyWords.forEach(word => {
        // 原有格式（事件系统）
        const uniqueKey = `${word.phrase.toLowerCase()}-${word.position.start}-${word.position.end}`;
        hiddenWordsMap[uniqueKey] = false; // 隐藏
        
        // 传统系统兼容格式
        const traditionalKey = `${word.original.toLowerCase()}-${index}`;
        hiddenWordsMap[traditionalKey] = false; // 隐藏
        
        // 简单格式
        const simpleKey = word.original.toLowerCase();
        hiddenWordsMap[simpleKey] = false; // 隐藏
      });
      
      // 已完成的词汇应该显示
      chunkState.practiceData.completedWords.forEach(completedWord => {
        vocabularyWords.forEach(word => {
          if (word.original.toLowerCase() === completedWord.toLowerCase()) {
            // 更新所有格式的key
            const uniqueKey = `${word.phrase.toLowerCase()}-${word.position.start}-${word.position.end}`;
            const traditionalKey = `${word.original.toLowerCase()}-${index}`;
            const simpleKey = word.original.toLowerCase();
            
            hiddenWordsMap[uniqueKey] = true; // 显示
            hiddenWordsMap[traditionalKey] = true; // 显示
            hiddenWordsMap[simpleKey] = true; // 显示
          }
        });
      });
    });

    // 合并用户交互状态（用户交互优先） - 🔥 修复内存泄漏：使用Map
    const filteredUserInteraction = Object.fromEntries(
      Array.from(ChunkStateMachine.globalUserInteractionMap.entries()).filter(([key]) => /-\d+(?:-\d+)?$/.test(key))
    );

    return {
      ...hiddenWordsMap,
      ...filteredUserInteraction
    };
  }

  updateChunkStatus(chunkId: string, newStatus: ChunkState['status'], context?: any) {
    const currentState = this.states.get(chunkId);
    if (!currentState) return;

    const now = Date.now();
    
    // 🔧 添加调试信息来跟踪状态变化
    // console.log(`🔄 更新chunk状态:`, {
    //   chunkId,
    //   oldStatus: currentState.status,
    //   newStatus,
    //   shouldTriggerDialog: currentState.metadata.shouldTriggerDialog,
    //   incompleteWords: currentState.practiceData.incompleteWords,
    //   completedWords: currentState.practiceData.completedWords
    // });
    
    // 🔥 关键修复：只有当chunk真正完成（没有未完成的单词）时才跳过重新激活
    if (currentState.status === 'completed' && newStatus === 'active') {
      const hasIncompleteWords = currentState.practiceData.incompleteWords.length > 0;
      if (!hasIncompleteWords) {
        console.log(`⚠️ Chunk ${chunkId} 已完成，跳过重新激活`);
        return;
      } else {
        console.log(`🔄 Chunk ${chunkId} 状态不一致，允许重新激活 (还有${currentState.practiceData.incompleteWords.length}个未完成单词)`);
      }
    }
    
    const updatedState: ChunkState = {
      ...currentState,
      status: newStatus,
      timestamps: {
        ...currentState.timestamps,
        lastVisit: now,
        firstVisit: currentState.timestamps.firstVisit || now,
        ...(newStatus === 'completed' && { completedAt: now })
      },
      metadata: {
        ...currentState.metadata,
        isVisited: true,
        // 🔥 关键修复：基于实际的完成状态来决定是否触发dialog
        // ✅ Shadowing 模式：一旦 shouldTriggerDialog 被设置为 false（用户点击 Continue），
        //    则在后续激活/重建期间保持为 false，避免再次弹出相同句子
        shouldTriggerDialog: (() => {
          if (newStatus === 'completed') return false;

          // 读取已保存的全局状态（跨重建持久化）
          const saved = ChunkStateMachine.globalCompletedStates.get(currentState.id);
          const persistedFalse = saved?.shouldTriggerDialog === false;

          if (persistedFalse) {
            return false;
          }

          if (!this.config.practiceMode) {
            return false;
          }

          if (this.config.practiceModeType === 'shadowing') {
            // Shadowing 模式：只要处于练习模式且有内容即可
            return Boolean(currentState.metadata.hasVocabularyWords);
          }

          // 词汇模式：按未完成词汇情况决定
          return currentState.practiceData.incompleteWords.length > 0;
        })()
      }
    };

    switch (newStatus) {
      case 'active':
        this.handleChunkActivation();
        break;
      case 'practicing':
        this.handlePracticeStart(updatedState, context);
        break;
      case 'completed':
        this.handleChunkCompletion(updatedState);
        break;
    }

    this.states.set(chunkId, updatedState);
    
    // 更新全局完成状态 - 🔥 修复内存泄漏：使用Map并限制大小
    const stateToSave = {
      completedWords: updatedState.practiceData.completedWords,
      shouldTriggerDialog: newStatus === 'completed' ? false : updatedState.metadata.shouldTriggerDialog
    };
    
    // 限制Map大小，防止内存泄漏
    if (ChunkStateMachine.globalCompletedStates.size >= ChunkStateMachine.MAX_CACHE_SIZE) {
      const keysToDelete = Array.from(ChunkStateMachine.globalCompletedStates.keys())
        .slice(0, Math.floor(ChunkStateMachine.MAX_CACHE_SIZE / 2));
      keysToDelete.forEach(key => ChunkStateMachine.globalCompletedStates.delete(key));
      console.log(`🧹 Cleaned ${keysToDelete.length} old completed state entries`);
    }
    
    ChunkStateMachine.globalCompletedStates.set(chunkId, stateToSave);
    
    if (newStatus === 'completed') {
      console.log(`✅ Chunk ${chunkId} 已标记为完成，shouldTriggerDialog设为false`);
    }
  }

  private handleChunkActivation() {
    // chunk激活时不触发dialog，只在接近结尾时触发（handlePracticeStart中处理）
    // console.log(`✅ Chunk ${state.index} 已激活，等待接近结尾时触发dialog`);
  }

  private handlePracticeStart(state: ChunkState, context: any) {
    // 获取当前chunk的词汇列表
    const vocabularyWords = this.getChunkVocabularyWords(state.index, context?.chunkText || '');

    const event: DialogTriggerEvent = {
      type: this.config.practiceModeType === 'shadowing' ? 'shadowing_practice' : 'vocabulary_quiz',
      chunkId: state.id,
      chunkIndex: state.index,
      triggerReason: 'chunk_near_end',
      payload: {
        incompleteWords: vocabularyWords,
        chunkText: context?.chunkText,
        translation: context?.translation,
        timeRemaining: context?.timeRemaining,
        // 为 Shadowing 模式添加句子信息
        sentences: this.config.practiceModeType === 'shadowing' ? [{
          text: context?.chunkText,
          translation: context?.translation,
          timestamp: context?.chunkTimestamp || [0, 3] // 使用实际chunk时间戳
        }] : undefined
      },
      timestamp: Date.now()
    };

    this.queueEvent(event);
  }

  private handleChunkCompletion(state: ChunkState) {
    const event: DialogTriggerEvent = {
      type: 'chunk_completed',
      chunkId: state.id,
      chunkIndex: state.index,
      triggerReason: 'all_words_completed',
      payload: {
        completionPercentage: state.practiceData.completionPercentage,
        totalAttempts: state.practiceData.attempts,
        completedWords: state.practiceData.completedWords
      },
      timestamp: Date.now()
    };

    this.emitEvent('chunk_completed', event);
  }


  private queueEvent(event: DialogTriggerEvent) {
    this.eventQueue.push(event);
    this.processEventQueue();
  }

  private processEventQueue() {
    if (this.eventQueue.length === 0) return;

    const event = this.eventQueue.shift()!;
    this.emitEvent(event.type, event);
  }

  private emitEvent(eventType: string, event: DialogTriggerEvent) {
    const typeListeners = this.listeners.get(eventType);
    if (typeListeners) {
      typeListeners.forEach(callback => callback(event));
    }

    const globalListeners = this.listeners.get('*');
    if (globalListeners) {
      globalListeners.forEach(callback => callback(event));
    }
  }

  addEventListener(eventType: string, callback: (event: DialogTriggerEvent) => void) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  getChunkState(chunkId: string): ChunkState | undefined {
    return this.states.get(chunkId);
  }

  getChunkId(chunkIndex: number): string {
    return `${this.groupId}-chunk-${chunkIndex}`;
  }

  markWordCompleted(chunkId: string, word: string) {
    const state = this.states.get(chunkId);
    if (!state) return;

    const updatedPracticeData = {
      ...state.practiceData,
      completedWords: [...state.practiceData.completedWords, word],
      incompleteWords: state.practiceData.incompleteWords.filter(w => w !== word),
      attempts: state.practiceData.attempts + 1
    };

    updatedPracticeData.completionPercentage = 
      (updatedPracticeData.completedWords.length / 
       (updatedPracticeData.completedWords.length + updatedPracticeData.incompleteWords.length)) * 100;

    const updatedState: ChunkState = {
      ...state,
      practiceData: updatedPracticeData,
      status: updatedPracticeData.completionPercentage >= 100 ? 'completed' : state.status,
      metadata: {
        ...state.metadata,
        // 当所有单词完成后，不再触发dialog
        shouldTriggerDialog: updatedPracticeData.completionPercentage >= 100 ? false : state.metadata.shouldTriggerDialog
      }
    };

    this.states.set(chunkId, updatedState);

    // 更新全局完成状态 - 🔥 修复内存泄漏：使用Map
    ChunkStateMachine.globalCompletedStates.set(chunkId, {
      completedWords: updatedState.practiceData.completedWords,
      shouldTriggerDialog: updatedState.metadata.shouldTriggerDialog
    });

    if (updatedState.status === 'completed') {
      this.handleChunkCompletion(updatedState);
    }

    return updatedState;
  }

  suppressChunkDialog(chunkId: string, options?: { markSkipped?: boolean }) {
    const state = this.states.get(chunkId);
    if (!state) return;

    const updatedState: ChunkState = {
      ...state,
      status: options?.markSkipped ? 'skipped' : state.status,
      metadata: {
        ...state.metadata,
        shouldTriggerDialog: false
      }
    };

    this.states.set(chunkId, updatedState);

    ChunkStateMachine.globalCompletedStates.set(chunkId, {
      completedWords: updatedState.practiceData.completedWords,
      shouldTriggerDialog: false
    });
  }

  // 获取隐藏词汇映射
  getHiddenWordsMap(chunkIndex: number): Record<string, boolean> {
    const chunkId = `chunk-${chunkIndex}`;
    const state = this.states.get(chunkId);
    if (!state || !this.config.practiceMode) return {};

    const hiddenWordsMap: Record<string, boolean> = {};
    
    // 获取当前chunk的所有词汇
    const allWords = this.extractVocabularyWords(
      // 需要从外部获取chunk文本，这里先返回空对象
      ""
    );
    
    // 默认所有词汇都隐藏（false表示隐藏，true表示显示）
    allWords.forEach(word => {
      const uniqueKey = `${word.toLowerCase()}-${chunkIndex}`;
      hiddenWordsMap[uniqueKey] = false;
    });
    
    // 已完成的词汇应该显示
    state.practiceData.completedWords.forEach(word => {
      const uniqueKey = `${word.toLowerCase()}-${chunkIndex}`;
      hiddenWordsMap[uniqueKey] = true;
    });

    return hiddenWordsMap;
  }

  // 获取chunk的词汇列表（用于显示在dialog中）
  getChunkVocabularyWords(chunkIndex: number, chunkText: string): any[] {

    const foundWords: any[] = [];

    const buildFlexibleRegex = (term: string): RegExp | null => {
      const trimmed = term?.trim();
      if (!trimmed) return null;

      const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const words = trimmed.split(/\s+/).map((word) => escapeRegex(word)).filter(Boolean);

      if (!words.length) return null;

      if (words.length === 1) {
        const pattern = words[0];
        return new RegExp(`\\b${pattern}\\b`, 'gi');
      }

      const pattern = words.join('\\s+');
      return new RegExp(`\\b${pattern}\\b`, 'gi');
    };

    // 优先使用预设词汇列表
    if (this.config.vocabularyTerms && this.config.vocabularyTerms.length > 0) {
      const text = chunkText;

      this.config.vocabularyTerms.forEach((term) => {
        const regex = buildFlexibleRegex(term);
        if (!regex) return;

        let match: RegExpExecArray | null;

        while ((match = regex.exec(text)) !== null) {
          const start = match.index ?? 0;
          const matchedText = match[0] ?? term;
          const end = start + matchedText.length;

          foundWords.push({
            original: term,
            phrase: matchedText,
            position: {
              start,
              end,
            },
            chunkIndex,
          });
        }
      });
    }

    // TOEFL模式：使用与PracticeWordCard完全相同的检测逻辑
    if (this.config.isWordCheckerReady && this.config.practiceModeType === 'toefl') {
      try {
        if (vocabularyLevelChecker && vocabularyLevelChecker.hasTranslation) {
          // 提取所有单词（使用与PracticeWordCard相同的正则）
          const words = chunkText.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g) || [];
          
          // 记录位置信息
          let currentPos = 0;
          
          words.forEach(word => {
            // 使用与PracticeWordCard完全相同的检测逻辑
            // 1. 检查是否为缩写
            const isContraction = /^'?(s|m|ve|ll|re|d)$/i.test(word);
            if (isContraction) return;
            
            // 2. 检查是否为有效英文单词
            const isValidWord = /^[a-zA-Z]+('[a-zA-Z]+)?$/i.test(word);
            if (!isValidWord) return;
            
            // 3. 检查是否有翻译
            try {
              const hasTranslation = vocabularyLevelChecker.hasTranslation(word);
              if (hasTranslation) {
                // 找到词汇在文本中的位置
                const wordPos = chunkText.indexOf(word, currentPos);
                if (wordPos !== -1) {
                  foundWords.push({
                    original: word,
                    phrase: word.toLowerCase(),
                    position: {
                      start: wordPos,
                      end: wordPos + word.length
                    },
                    chunkIndex
                  });
                  currentPos = wordPos + word.length;
                }
              }
            } catch (error) {
              console.warn(`检查单词 "${word}" 时出错:`, error);
            }
          });
          
        }
      } catch (error) {
        console.error('❌ TOEFL词汇检测失败:', error);
      }
    }


    return foundWords;
  }

  // 新增：获取chunk文本中的隐藏词汇映射（需要chunk文本参数）
  getChunkHiddenWordsMap(chunkIndex: number, chunkText: string): Record<string, boolean> {
    const chunkId = `${this.groupId}-chunk-${chunkIndex}`;
    const state = this.states.get(chunkId);
    if (!state || !this.config.practiceMode) return {};

    const hiddenWordsMap: Record<string, boolean> = {};
    
    // 获取当前chunk的所有词汇
    const allWords = this.getChunkVocabularyWords(chunkIndex, chunkText);
    
    // 默认所有词汇都隐藏（false表示隐藏，true表示显示）
    allWords.forEach(word => {
      // 只生成当前chunk特有的key格式，避免覆盖其他chunk的状态
      const uniqueKey = `${word.phrase.toLowerCase()}-${word.position.start}-${word.position.end}`;
      const traditionalKey = `${word.original.toLowerCase()}-${chunkIndex}`;
      // 🔥 关键修复：不再生成简单格式的key，避免覆盖全局状态
      
      hiddenWordsMap[uniqueKey] = false;
      hiddenWordsMap[traditionalKey] = false;
    });
    
    // 已完成的词汇应该显示
    state.practiceData.completedWords.forEach(completedWord => {
      allWords.forEach(word => {
        if (word.original.toLowerCase() === completedWord.toLowerCase()) {
          const uniqueKey = `${word.phrase.toLowerCase()}-${word.position.start}-${word.position.end}`;
          const traditionalKey = `${word.original.toLowerCase()}-${chunkIndex}`;
          // 🔥 关键修复：不再生成简单格式的key，避免覆盖全局状态
          
          hiddenWordsMap[uniqueKey] = true;
          hiddenWordsMap[traditionalKey] = true;
        }
      });
    });

    return hiddenWordsMap;
  }

  private extractVocabularyWords(text: string): string[] {

    // Vocabulary模式：使用预设词汇列表
    if (this.config.practiceModeType === 'vocabulary' && this.config.vocabularyTerms && this.config.vocabularyTerms.length > 0) {
      const foundTerms = this.config.vocabularyTerms.filter(term => {
        const termLower = term.toLowerCase();
        const textLower = text.toLowerCase();
        const regex = new RegExp(`\\b${termLower}\\b`);
        return regex.test(textLower);
      });
      return foundTerms;
    }

    // TOEFL模式：使用与PracticeWordCard相同的检测逻辑
    if (this.config.isWordCheckerReady && this.config.practiceModeType === 'toefl') {
      try {
        if (vocabularyLevelChecker && vocabularyLevelChecker.hasTranslation) {
          const words = text.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g) || [];
          
          const foundWords = words.filter(word => {
            const isContraction = /^'?(s|m|ve|ll|re|d)$/i.test(word);
            if (isContraction) return false;
            
            const isValidWord = /^[a-zA-Z]+('[a-zA-Z]+)?$/i.test(word);
            if (!isValidWord) return false;
            
            try {
              return vocabularyLevelChecker.hasTranslation(word);
            } catch {
              return false;
            }
          });
          
          const uniqueWords = Array.from(new Set(foundWords));
          return uniqueWords;
        }
      } catch {
        // Silent error handling
      }
    }

    return [];
  }

  // 更新用户交互状态（点击reveal等）
  updateUserInteraction(wordKey: string, isVisible: boolean | null) {
    // 🔥 修复内存泄漏：限制Map大小，防止无限增长
    if (ChunkStateMachine.globalUserInteractionMap.size >= ChunkStateMachine.MAX_CACHE_SIZE) {
      // 删除最旧的一半项目
      const keysToDelete = Array.from(ChunkStateMachine.globalUserInteractionMap.keys())
        .slice(0, Math.floor(ChunkStateMachine.MAX_CACHE_SIZE / 2));
      keysToDelete.forEach(key => ChunkStateMachine.globalUserInteractionMap.delete(key));
      console.log(`🧹 Cleaned ${keysToDelete.length} old user interaction entries`);
    }

    if (isVisible === null) {
      ChunkStateMachine.globalUserInteractionMap.delete(wordKey);
    } else {
      ChunkStateMachine.globalUserInteractionMap.set(wordKey, isVisible);
    }
  }

  // 获取用户交互状态
  getUserInteractionMap(): Record<string, boolean> {
    return Object.fromEntries(ChunkStateMachine.globalUserInteractionMap);
  }

  updateConfig(newConfig: Partial<typeof this.config>) {
    const oldConfig = { ...this.config };
    this.config = { ...this.config, ...newConfig };
    
    // 如果关键配置发生变化，清除状态以触发重新初始化
    const shouldReinitialize = 
      oldConfig.vocabularyTerms !== newConfig.vocabularyTerms ||
      oldConfig.practiceModeType !== newConfig.practiceModeType;
    
    if (shouldReinitialize && this.config.practiceMode) {
      this.states.clear();
      // 不清除全局用户交互状态，保持用户的学习进度
    }
  }

  destroy() {
    this.states.clear();
    this.eventQueue.length = 0;
    this.listeners.clear();
  }
}

// ===== 播放器事件管理器 =====
class PlayerEventManager {
  private player: any;
  private chunks: any[];
  private stateMachine: ChunkStateMachine;
  private currentChunkIndex = -1;
  private cueDrivenMode = false;
  private isUserSeeking = false;
  private seekingTimeout: NodeJS.Timeout | null = null;
  private pollInterval: NodeJS.Timeout | null = null;
  private lastPolledTime = 0;
  private lastTimeDebugLog = 0;

  constructor(player: any, chunks: any[], stateMachine: ChunkStateMachine) {
    this.player = player;
    this.chunks = chunks;
    this.stateMachine = stateMachine;
    this.setupPlayerEventListeners();
  }

  private setupPlayerEventListeners() {
    // YouTube player不支持原生事件，将通过外部时间更新驱动
  }

  // 外部时间更新接口，从currentTime prop调用
  public receiveTimeUpdate(currentTime: number) {
    if (this.cueDrivenMode) {
      return;
    }
    // 节流：只有时间变化超过0.1秒才处理
    if (Math.abs(currentTime - this.lastPolledTime) > 0.1) {
      this.handleTimeUpdate(currentTime);
      this.lastPolledTime = currentTime;
    }
  }

  public setCueDrivenMode(enabled: boolean) {
    if (this.cueDrivenMode === enabled) {
      return;
    }
    this.cueDrivenMode = enabled;
    if (!enabled) {
      this.currentChunkIndex = -1;
    }
  }

  public syncCurrentChunkIndex(index: number) {
    this.currentChunkIndex = index;
  }

  private handleTimeUpdate(currentTime: number) {
    if (this.isUserSeeking) return;

    const newChunkIndex = this.findCurrentChunkIndex(currentTime);
    
    if (newChunkIndex !== this.currentChunkIndex) {
      this.handleChunkChange(this.currentChunkIndex, newChunkIndex, currentTime);
      this.currentChunkIndex = newChunkIndex;
    }

    if (newChunkIndex !== -1) {
      this.checkChunkEndApproaching(newChunkIndex, currentTime);
    }
  }

  private handleChunkChange(oldIndex: number, newIndex: number, currentTime: number) {
    if (oldIndex !== -1) {
      const oldChunkId = this.stateMachine.getChunkId(oldIndex);
      const oldChunkData = this.chunks[oldIndex];
      const oldChunkState = this.stateMachine.getChunkState(oldChunkId);

      const shouldTriggerOnExit = Boolean(
        oldChunkState &&
        oldChunkState.status === 'active' &&
        oldChunkState.metadata.shouldTriggerDialog
      );

      if (shouldTriggerOnExit) {
        this.stateMachine.updateChunkStatus(oldChunkId, 'practicing', {
          triggerReason: 'chunk_end',
          timeRemaining: 0,
          chunkText: oldChunkData?.text,
          translation: oldChunkData?.translation,
          chunkTimestamp: oldChunkData?.timestamp
        });
      }

      this.stateMachine.updateChunkStatus(oldChunkId, 'pending');
    }

    if (newIndex !== -1) {
      const newChunkId = this.stateMachine.getChunkId(newIndex);
      const chunkData = this.chunks[newIndex];
      
      // 🔧 添加调试信息来跟踪chunk激活
      // const state = this.stateMachine.getChunkState(newChunkId);
      // console.log(`📍 进入chunk ${newIndex}:`, {
      //   chunkId: newChunkId,
      //   textPreview: chunkData.text.substring(0, 50) + '...',
      //   currentStatus: state?.status,
      //   shouldTriggerDialog: state?.metadata.shouldTriggerDialog,
      //   incompleteWords: state?.practiceData.incompleteWords,
      //   completedWords: state?.practiceData.completedWords
      // });
      
      this.stateMachine.updateChunkStatus(newChunkId, 'active', {
        chunkText: chunkData.text,
        translation: chunkData.translation,
        currentTime
      });
    }
  }

  private checkChunkEndApproaching(chunkIndex: number, currentTime: number) {
    const chunk = this.chunks[chunkIndex];
    if (!chunk) return;

    const timeRemaining = chunk.timestamp[1] - currentTime;
    const isNearEnd = timeRemaining <= 0.3 && timeRemaining > 0;

    // 添加更详细的时间调试信息
    if (process.env.NODE_ENV === 'development') {
      // 每2秒打印一次时间状态，避免日志过多
      const now = Date.now();
      if (!this.lastTimeDebugLog || now - this.lastTimeDebugLog > 2000) {
        this.lastTimeDebugLog = now;
      }
    }

    if (isNearEnd) {
      const chunkId = this.stateMachine.getChunkId(chunkIndex);
      const state = this.stateMachine.getChunkState(chunkId);
      
      // 🔧 添加调试信息来诊断dialog不弹出的问题
      // console.log(`🔍 Chunk ${chunkIndex} 接近结尾:`, {
      //   chunkId,
      //   shouldTriggerDialog: state?.metadata.shouldTriggerDialog,
      //   status: state?.status,
      //   incompleteWords: state?.practiceData.incompleteWords,
      //   completedWords: state?.practiceData.completedWords,
      //   completionPercentage: state?.practiceData.completionPercentage,
      //   willTriggerDialog: state?.metadata.shouldTriggerDialog && state.status === 'active'
      // });
      
      if (state?.metadata.shouldTriggerDialog && state.status === 'active') {
        // console.log(`✅ 触发dialog for chunk ${chunkIndex}`);
        this.stateMachine.updateChunkStatus(chunkId, 'practicing', {
          triggerReason: 'near_end',
          timeRemaining,
          chunkText: chunk.text,
          translation: chunk.translation,
          chunkTimestamp: chunk.timestamp // 传递实际的chunk时间戳
        });
      } 
      // else {
      //   console.log(`❌ 不触发dialog for chunk ${chunkIndex}`, {
      //     reason: !state?.metadata.shouldTriggerDialog ? 'shouldTriggerDialog=false' : 'status不是active'
      //   });
      // }
    }
  }

  private handleSeeking() {
    this.isUserSeeking = true;
    if (this.seekingTimeout) {
      clearTimeout(this.seekingTimeout);
    }
  }

  private handleSeeked() {
    this.seekingTimeout = setTimeout(() => {
      this.isUserSeeking = false;
    }, 500);
  }

  private handlePlay() {
    // 播放状态处理
  }

  private handlePause() {
    // 暂停状态处理
  }

  private findCurrentChunkIndex(currentTime: number): number {
    return this.chunks.findIndex(chunk =>
      currentTime >= chunk.timestamp[0] && currentTime <= chunk.timestamp[1]
    );
  }

  private setupPollingFallback() {
    // 不再使用轮询，改为外部驱动
  }

  destroy() {
    // 清理事件监听器
    if (this.player.off && typeof this.player.off === 'function') {
      this.player.off('timeupdate', this.handleTimeUpdate);
      this.player.off('seeking', this.handleSeeking);
      this.player.off('seeked', this.handleSeeked);
      this.player.off('play', this.handlePlay);
      this.player.off('pause', this.handlePause);
    }

    // 清理轮询
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }

    // 清理定时器
    if (this.seekingTimeout) {
      clearTimeout(this.seekingTimeout);
      this.seekingTimeout = null;
    }
  }
}

// ===== React Hook 封装 =====
function useEventDrivenDialogSystem({
  chunks,
  player,
  practiceMode,
  practiceModeType,
  vocabularyTerms,
  isWordCheckerReady,
  onDialogTrigger
}: {
  chunks: any[];
  player: any;
  practiceMode: boolean;
  practiceModeType: 'vocabulary' | 'toefl' | 'shadowing';
  vocabularyTerms: string[];
  isWordCheckerReady: boolean;
  onDialogTrigger: (event: DialogTriggerEvent) => void;
}) {
  const stateMachineRef = useRef<ChunkStateMachine | null>(null);
  const playerManagerRef = useRef<PlayerEventManager | null>(null);
  const cueRegistrationRef = useRef<(() => void) | null>(null);
  const cueSourceIdRef = useRef<string>(`practice-cues-${Math.random().toString(36).slice(2)}`);
  const [systemState, setSystemState] = useState({
    initialized: false,
    currentChunk: null as ChunkState | null,
    lastUpdate: Date.now(), // 添加lastUpdate属性用于触发重新渲染
  });

  // 稳定的chunks引用
  const stableChunks = useMemo(() => chunks, [chunks]);

  // 稳定的player引用
  const stablePlayer = useRef(player);
  if (player && player !== stablePlayer.current) {
    stablePlayer.current = player;
  }

  // 稳定的onDialogTrigger引用
  const stableOnDialogTrigger = useRef(onDialogTrigger);
  stableOnDialogTrigger.current = onDialogTrigger;

  // 一次性初始化标志
  const initializationDone = useRef(false);
  const lastInitParams = useRef<string>('');

  // 初始化系统 - 在所有practice mode下初始化
  useEffect(() => {
    if (!practiceMode || !stablePlayer.current || stableChunks.length === 0) {
      // 如果练习模式关闭或缺少必要数据，重置初始化标志并更新状态
      if (!practiceMode) {
        initializationDone.current = false;
        lastInitParams.current = '';
      }
      setSystemState(prev => prev.initialized ? { ...prev, initialized: false } : prev);
      return;
    }

    // 检查关键参数是否真的变化了
    const currentParams = `${practiceMode}-${stableChunks.length}-${practiceModeType}-${vocabularyTerms.join(',')}-${isWordCheckerReady}`;
    
    // 更宽松的初始化控制 - 允许关键参数变化时重新初始化
    if (initializationDone.current && lastInitParams.current === currentParams && stateMachineRef.current) {
      return;
    }

    // 标记初始化开始
    initializationDone.current = true;
    lastInitParams.current = currentParams;

    // 创建状态机，使用group的timestamp作为唯一ID
    const groupId = `group-${stableChunks[0]?.timestamp[0] || Date.now()}`;
    stateMachineRef.current = new ChunkStateMachine(stableChunks, {
      practiceMode,
      practiceModeType,
      vocabularyTerms,
      isWordCheckerReady
    }, groupId);

    // 创建播放器事件管理器
    playerManagerRef.current = new PlayerEventManager(
      stablePlayer.current,
      stableChunks,
      stateMachineRef.current
    );

    // 注册事件监听器，使用稳定的引用
    const unsubscribeVocab = stateMachineRef.current.addEventListener(
      'vocabulary_quiz',
      (event) => stableOnDialogTrigger.current(event)
    );

    const unsubscribeShadowing = stateMachineRef.current.addEventListener(
      'shadowing_practice',
      (event) => stableOnDialogTrigger.current(event)
    );

    const unsubscribeCompleted = stateMachineRef.current.addEventListener(
      'chunk_completed',
      (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Chunk completed:', event);
        }
      }
    );

    setSystemState(prev => ({ ...prev, initialized: true }));

    return () => {
      unsubscribeVocab();
      unsubscribeShadowing();
      unsubscribeCompleted();
      playerManagerRef.current?.destroy();
      stateMachineRef.current?.destroy();
      stateMachineRef.current = null;
      playerManagerRef.current = null;
      // 重置初始化标志，允许重新初始化
      initializationDone.current = false;
      lastInitParams.current = '';
    };
  }, [practiceMode, practiceModeType, vocabularyTerms, isWordCheckerReady, stableChunks]); // 依赖关键参数，允许必要时重新初始化

  // 更新配置
  useEffect(() => {
    if (stateMachineRef.current) {
      stateMachineRef.current.updateConfig({
        practiceMode,
        practiceModeType,
        vocabularyTerms,
        isWordCheckerReady
      });
    }
  }, [practiceMode, practiceModeType, vocabularyTerms, isWordCheckerReady]);

  useEffect(() => {
    if (cueRegistrationRef.current) {
      cueRegistrationRef.current();
      cueRegistrationRef.current = null;
    }
    playerManagerRef.current?.setCueDrivenMode(false);

    if (
      !practiceMode ||
      !systemState.initialized ||
      !stateMachineRef.current ||
      !stableChunks.length ||
      !mediaCueRegistry.hasCueSupport()
    ) {
      return;
    }

    const machine = stateMachineRef.current;
    const descriptors = stableChunks
      .map((chunk, index) => {
        const stamp = Array.isArray(chunk.timestamp) ? chunk.timestamp : [];
        const rawStart = Number(stamp[0]);
        const rawEnd = Number(stamp[1]);
        if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd)) {
          return null;
        }
        const start = rawStart;
        const end = rawEnd > rawStart ? rawEnd : rawStart + 0.1;
        const chunkId = machine.getChunkId(index);
        return {
          id: chunkId,
          start,
          end,
          data: {
            chunkId,
            chunkIndex: index,
            chunkText: chunk.text,
            translation: chunk.translation,
            timestamp: stamp
          }
        };
      })
      .filter(Boolean) as Array<{
        id: string;
        start: number;
        end: number;
        data: Record<string, unknown>;
      }>;

    if (!descriptors.length) {
      return;
    }

    const unregister = mediaCueRegistry.registerCueSet(
      cueSourceIdRef.current,
      descriptors,
      {
        onEnter(descriptor) {
          const machineInstance = stateMachineRef.current;
          if (!machineInstance) return;
          const data = descriptor.data || {};
          const chunkIndex = data.chunkIndex as number | undefined;
          const chunkId =
            (data.chunkId as string | undefined) ??
            (typeof chunkIndex === 'number'
              ? machineInstance.getChunkId(chunkIndex)
              : undefined);
          if (!chunkId || typeof chunkIndex !== 'number') return;
          const chunkText =
            (data.chunkText as string | undefined) ??
            stableChunks[chunkIndex]?.text ??
            '';
          const translation =
            (data.translation as string | undefined) ??
            stableChunks[chunkIndex]?.translation;

          playerManagerRef.current?.syncCurrentChunkIndex(chunkIndex);

          machineInstance.updateChunkStatus(chunkId, 'active', {
            chunkText,
            translation,
            currentTime: descriptor.start,
            chunkTimestamp: data.timestamp
          });
        },
        onExit(descriptor) {
          const machineInstance = stateMachineRef.current;
          if (!machineInstance) return;
          const data = descriptor.data || {};
          const chunkIndex = data.chunkIndex as number | undefined;
          const chunkId =
            (data.chunkId as string | undefined) ??
            (typeof chunkIndex === 'number'
              ? machineInstance.getChunkId(chunkIndex)
              : undefined);
          if (!chunkId || typeof chunkIndex !== 'number') return;
          const chunkText =
            (data.chunkText as string | undefined) ??
            stableChunks[chunkIndex]?.text ??
            '';
          const translation =
            (data.translation as string | undefined) ??
            stableChunks[chunkIndex]?.translation;
          const timestamp =
            (Array.isArray(data.timestamp) ? data.timestamp : undefined) ??
            stableChunks[chunkIndex]?.timestamp;

          const state = machineInstance.getChunkState(chunkId);
          if (state?.status === 'active' && state.metadata.shouldTriggerDialog) {
            machineInstance.updateChunkStatus(chunkId, 'practicing', {
              triggerReason: 'cue_exit',
              timeRemaining: 0,
              chunkText,
              translation,
              chunkTimestamp: timestamp
            });
          }

          machineInstance.updateChunkStatus(chunkId, 'pending', {
            triggerReason: 'cue_exit'
          });

          playerManagerRef.current?.syncCurrentChunkIndex(-1);
        }
      },
      {
        onAttachChange(attached) {
          playerManagerRef.current?.setCueDrivenMode(attached);
          if (!attached) {
            playerManagerRef.current?.syncCurrentChunkIndex(-1);
          }
        }
      }
    );

    cueRegistrationRef.current = () => {
      unregister();
      playerManagerRef.current?.setCueDrivenMode(false);
      playerManagerRef.current?.syncCurrentChunkIndex(-1);
    };

    return () => {
      unregister();
      playerManagerRef.current?.setCueDrivenMode(false);
      playerManagerRef.current?.syncCurrentChunkIndex(-1);
      cueRegistrationRef.current = null;
    };
  }, [practiceMode, systemState.initialized, stableChunks]);

  const markWordCompleted = useCallback((chunkId: string, word: string) => {
    return stateMachineRef.current?.markWordCompleted(chunkId, word);
  }, []);

  const getChunkState = useCallback((chunkId: string) => {
    return stateMachineRef.current?.getChunkState(chunkId);
  }, []);

  const getAllHiddenWordsMap = useCallback((chunks: any[]) => {
    return stateMachineRef.current?.getAllHiddenWordsMap(chunks) || {};
  }, []);

  const getChunkHiddenWordsMap = useCallback((chunkIndex: number, chunkText: string) => {
    return stateMachineRef.current?.getChunkHiddenWordsMap(chunkIndex, chunkText) || {};
  }, []);

  const getChunkVocabularyWords = useCallback((chunkIndex: number, chunkText: string) => {
    return stateMachineRef.current?.getChunkVocabularyWords(chunkIndex, chunkText) || [];
  }, []);

  // 直接导出findPhraseInText函数，避免间接调用
  const findPhraseInText = useCallback((text: string, chunkIndex: number = -1) => {
    return stateMachineRef.current?.getChunkVocabularyWords(chunkIndex, text) || [];
  }, []);

  // 用户交互状态更新方法
  const updateUserInteraction = useCallback((wordKey: string, isVisible: boolean) => {
    stateMachineRef.current?.updateUserInteraction(wordKey, isVisible);
    // 强制触发状态更新以重新渲染
    setSystemState(prev => ({ ...prev, lastUpdate: Date.now() }));
  }, []);

  // 暴露时间更新方法
  const receiveTimeUpdate = useCallback((currentTime: number) => {
    if (playerManagerRef.current && practiceMode) {
      playerManagerRef.current.receiveTimeUpdate(currentTime);
    }
  }, [practiceMode]);

  const forceRefresh = useCallback(() => {
    setSystemState(prev => ({ ...prev, lastUpdate: Date.now() }));
  }, []);

  return {
    systemState,
    markWordCompleted,
    getChunkState,
    getAllHiddenWordsMap,
    getChunkHiddenWordsMap,
    getChunkVocabularyWords,
    findPhraseInText, // 新增：直接导出，避免间接调用
    updateUserInteraction, // 新增：用户交互状态更新
    receiveTimeUpdate, // 新增：外部时间更新接口
    stateMachine: stateMachineRef.current,
    // 保留getStateMachine供其他用途
    getStateMachine: () => stateMachineRef.current,
    forceRefresh
  };
}

// ===== Vocabulary 高亮类型 =====
export interface ActiveVocabularyTermData {
  term: string;
  textClass?: string;
  backgroundClass?: string;
  lightBackgroundClass?: string;
  level?: string;
  // 🎯 用于 hover card 显示的 vocabulary 数据
  explanation?: string | Record<string, string>;
  context_description?: string;
  example?: {
    sentence?: string;
    translation?: string | Record<string, string>;
  };
}

interface VocabularyHighlightStyle {
  textClass?: string;
  level?: string;
  showBadge?: boolean;
  // 🎯 完整的 vocabulary term（可能是多词短语，如 "planning their time off"）
  fullTerm?: string;
  // 🎯 预填充的 vocabulary 数据，用于 hover card 显示（不调用翻译 API）
  explanation?: string | Record<string, string>;
  context_description?: string;
  example?: {
    sentence?: string;
    translation?: string | Record<string, string>;
  };
}

const DEFAULT_VOCAB_HIGHLIGHT_TEXT_CLASS = "text-sky-400";
const VOCAB_SUFFIXES = ["ed", "ing", "s", "er", "est", "ly"] as const;

// ===== 组件Props接口 =====
interface GroupedTranscriptEntryProps {
  userSettings?: UserSettings;
  group: {
    text: string;
    translation?: string | Record<string, string>;
    timestamp: [number, number];
    chunks?: {
      text: string;
      translation?: string | Record<string, string>;
      timestamp: [number, number];
      sentences?: {
        text: string;
        translation?: string | Record<string, string>;
        timestamp: [number, number];
      }[];
    }[];
    sentences?: {
      text: string;
      translation?: string | Record<string, string>;
      timestamp: [number, number];
    }[];
  };
  isActive: boolean;
  currentTime: number;
  onSeek?: (time: number) => void;
  player?: {
    seekTo: (time: number) => void;
    setPlaybackRate: (speed: number) => void;
    getCurrentTime: () => number;
    play: () => Promise<void> | void;
    pause: () => void;
    on?: (event: string, callback: (...args: any[]) => void) => void;
    off?: (event: string, callback: (...args: any[]) => void) => void;
  };
  wordHighlightEnabled?: boolean;
  practiceMode?: boolean;
  practiceModeType?: "vocabulary" | "toefl" | "shadowing";
  vocabularyTerms?: string[];
  setIsPlaying?: (playing: boolean) => void;
  showTranslation?: boolean;
  isWordCheckerReady?: boolean;
  isVideoMinimized?: boolean;
  episodeId?: string;
  episodeTitle?: string; // 新增：episode标题
  episodeThumbnail?: string; // 新增：episode缩略图
  isImmersiveMode?: boolean;
  // 🎯 Vocabulary 高亮相关 props
  activeVocabularyTerms?: ActiveVocabularyTermData[];
  staticVocabularyTerms?: ActiveVocabularyTermData[];
}

// ===== 主组件 =====
export function GroupedTranscriptEntry({
  group,
  isActive,
  currentTime,
  onSeek,
  player,
  wordHighlightEnabled = true,
  practiceMode = false,
  practiceModeType = "vocabulary",
  vocabularyTerms = [],
  setIsPlaying,
  showTranslation,
  userSettings,
  isWordCheckerReady,
  isVideoMinimized = false,
  episodeId,
  episodeTitle,
  episodeThumbnail,
  isImmersiveMode = false,
  // 🎯 Vocabulary 高亮
  activeVocabularyTerms,
  staticVocabularyTerms,
}: GroupedTranscriptEntryProps) {
  const { t } = useI18n();

  // Helper to extract translation string
  const getTranslationText = useCallback((translation?: string | Record<string, string>) => {
    if (!translation) return "";
    if (typeof translation === 'string') return translation;
    
    const lang = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
    return translation[lang] || translation['zh'] || Object.values(translation)[0] || "";
  }, [userSettings?.languageSetting]);

  const statusOptions = useMemo<StatusOption[]>(() => {
    return STATUS_OPTION_CONFIG.map((option) => ({
      value: option.value,
      label: t(option.labelKey, option.defaultLabel),
      description: t(option.descriptionKey, option.defaultDescription),
    }));
  }, [t]);

  const [selectedStatusValue, setSelectedStatusValue] = useState<StatusValue>(STATUS_OPTION_CONFIG[0].value);

  const selectedStatusOption = useMemo(() => {
    return (
      statusOptions.find((option) => option.value === selectedStatusValue) ??
      statusOptions[0] ?? {
        value: STATUS_OPTION_CONFIG[0].value,
        label: t(STATUS_OPTION_CONFIG[0].labelKey, STATUS_OPTION_CONFIG[0].defaultLabel),
        description: t(STATUS_OPTION_CONFIG[0].descriptionKey, STATUS_OPTION_CONFIG[0].defaultDescription),
      }
    );
  }, [statusOptions, selectedStatusValue, t]);

  useEffect(() => {
    if (statusOptions.length === 0) {
      return;
    }
    const hasCurrent = statusOptions.some((option) => option.value === selectedStatusValue);
    if (!hasCurrent) {
      setSelectedStatusValue(statusOptions[0].value);
    }
  }, [statusOptions, selectedStatusValue]);

  const StatusIconMobile = selectedStatusValue === 'MASTERED'
    ? Check
    : selectedStatusValue === 'FUZZY'
      ? Lightbulb
      : Sparkles;
  // ===== 性能监控 =====
  useEffect(() => {
    const startTime = performance.now();
    return () => {
      const duration = performance.now() - startTime;
      vocabularyErrorHandler.recordPerformance("averageResponseTime", duration);
    };
  }, []);

  // ===== 基础状态和引用 =====
  const entryRef = useRef<HTMLDivElement>(null);
  const activeSpanRef = useRef<HTMLSpanElement | null>(null);
  const continueButtonRef = useRef<HTMLButtonElement>(null);
  const vocabCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const speakerRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isMobile = useIsMobile();
  const { user } = useUser();
  const globalPlayer = usePlayer();
  const playbackLockReleasesRef = useRef<Map<string, () => void>>(new Map());

  // ===== Dialog状态管理 =====
  const [dialogState, setDialogState] = useState({
    showVocabDialog: false,
    showShadowingDialog: false,
    currentEvent: null as DialogTriggerEvent | null,
    savedVocabWords: [] as any[],
    completedWords: {} as Record<string, boolean>,
    allWordsCompleted: false,
    activeWordIndex: 0,
    showQuizTranslation: false,
    revealedHiddenWords: {} as Record<string, boolean>,
  });
  const dialogStateRef = useRef(dialogState);
  useEffect(() => {
    dialogStateRef.current = dialogState;
  }, [dialogState]);

  const currentQuizTranslation = useMemo(() => {
    if (!dialogState.currentEvent) {
      return "";
    }

    const payloadTranslation = dialogState.currentEvent.payload?.translation;
    const payloadTranslationText = getTranslationText(payloadTranslation);
    
    if (payloadTranslationText.trim()) {
      return payloadTranslationText.trim();
    }

    const chunkIndex = dialogState.currentEvent.chunkIndex;
    const fallback = typeof chunkIndex === "number"
      ? group.chunks?.[chunkIndex]?.translation
      : undefined;
    const fallbackText = getTranslationText(fallback);

    if (fallbackText.trim()) {
      return fallbackText.trim();
    }

    return "";
  }, [dialogState.currentEvent, group.chunks, getTranslationText]);
  const hasQuizTranslation = Boolean(currentQuizTranslation);
  const vocabularyDisplayName = useMemo(() => {
    if (!userSettings?.dictionarySetting) {
      return undefined;
    }
    return resolveDictionaryDisplayName(userSettings.dictionarySetting);
  }, [userSettings?.dictionarySetting]);

  // ===== Shadowing相关状态 =====
  const [shadowingState, setShadowingState] = useState({
    currentSentence: null as any,
    processedSentences: new Set<string>(),
    showTranslation: false,
    showSentence: false, // 新增：控制句子内容显示
    playbackSpeed: 1.0,
    showTranscription: false, // 控制是否显示转录结果
    transcriptionAccuracy: null as number | null, // 准确度分数
  });

  const shadowingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const vocabularyReplayTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const replayCueCleanupRef = useRef<(() => void) | null>(null);

  // ===== 录音功能 Hook =====
  const {
    // audioUrl,
    // audioBlob,
    isRecording,
    startRecording,
    stopRecording,
    transcribedText,
    isTranscribing,
    transcriptionError,
    audioLevel,
  } = useAudioRecorder();

  // ===== Shadowing统计记录 Hook =====
  const { saveRecord, saving } = useSaveShadowingRecord();
  const recordingStartTimeRef = useRef<number | null>(null);
  const [, forcePracticeRender] = useState(0);

  // ===== 计算句子相似度 (Enhanced version with contraction handling) =====
  const calculateSimilarity = useCallback((text1: string, text2: string): number => {
    return calculateOptimalSimilarity(text1, text2);
  }, []);

  // 使用 useRef 来跟踪是否已经保存过当前的转写结果
  const lastSavedTranscriptionRef = useRef<string | null>(null);

  // 监听转录结果并计算准确度，同时保存到统计系统
  useEffect(() => {
    if (transcribedText && shadowingState.currentSentence?.text) {
      const accuracy = calculateSimilarity(
        transcribedText,
        shadowingState.currentSentence.text
      );
      setShadowingState(prev => ({ ...prev, transcriptionAccuracy: accuracy }));

      // 保存练习记录到统计系统 - 只保存一次，避免重复
      // 检查是否是新的转写结果
      const transcriptionKey = `${transcribedText}-${shadowingState.currentSentence.text}`;
      if (episodeId && !saving && transcriptionKey !== lastSavedTranscriptionRef.current) {
        lastSavedTranscriptionRef.current = transcriptionKey; // 记录已保存的组合

        const duration = recordingStartTimeRef.current
          ? (Date.now() - recordingStartTimeRef.current) / 1000
          : 3; // 默认3秒

        // 使用传入的episode标题，如果没有则使用默认值
        const recordEpisodeTitle = episodeTitle || 'Unknown Episode';
        const recordEpisodeThumbnail = episodeThumbnail || '';

        saveRecord({
          userId: user?.id,
          episodeId,
          episodeTitle: recordEpisodeTitle,
          episodeThumbnail: recordEpisodeThumbnail,
          sentenceText: shadowingState.currentSentence.text,
          userTranscription: transcribedText,
          accuracy,
          duration
        }).catch(error => {
          console.error('Failed to save shadowing record:', error);
          lastSavedTranscriptionRef.current = null; // 失败时重置，允许重试
        });
      }
    }
  }, [
    transcribedText,
    // 移除 shadowingState.currentSentence?.text 避免循环
    // 只依赖必要的稳定值
    episodeId,
    saving,
    saveRecord,
    user?.id,
    calculateSimilarity
  ]);


  // ===== UI状态管理 =====
  const {
    uiState,
    setHoveredChunk,
    setActiveChunk,
    setShowToolbar,
    setShowButton,
    setShowAnalysis,
    // setAnalysisContent,
    setIsAnalyzing,
    setSelectedWord,
    setShowWordDrawer,
    setLastTapTime,
    setToolbarPosition,
    tapTimerRef,
    cleanup,
  } = useUIState();

  const defaultStatusValue = statusOptions[0]?.value ?? STATUS_OPTION_CONFIG[0].value;

  useEffect(() => {
    setSelectedStatusValue(prev => (prev === defaultStatusValue ? prev : defaultStatusValue));
  }, [uiState.selectedWord, defaultStatusValue]);

  // ===== 新增：高亮chunk状态 =====
  const [, setHighlightedChunkIndex] = useState<number | null>(null);
  
  // 清除高亮状态（点击其他地方时）
  const clearHighlight = useCallback(() => {
    setHighlightedChunkIndex(null);
  }, []);
  
  // 监听点击事件清除高亮
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // 如果点击的不是高亮的单词或FloatingToolbar，则清除高亮
      if (!target.closest('[data-word-highlight]') && 
          !target.closest('[data-floating-toolbar]') &&
          !target.closest('[data-floating-button]')) {
        clearHighlight();
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [clearHighlight]);

  // ===== 词汇解释Hook =====
  const {
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
  } = useWordExplanation(userSettings, group.text, user?.id);

  // ===== 有道翻译Hook =====
  const { translations, loading: youdaoLoading, getTranslation, isInitialized } = useYoudaoTranslation();

  const selectedWordExplanation = uiState.selectedWord ? explanations[uiState.selectedWord] : undefined;
  const selectedLookupMetadata = selectedWordExplanation?.lookupMetadata;
  const hasSelectedLookupMetadata = Boolean(
    selectedLookupMetadata && (
      (typeof selectedLookupMetadata.definition === 'string' && selectedLookupMetadata.definition.trim())
      || selectedLookupMetadata.tag
      || (typeof selectedLookupMetadata.bnc === 'number' && selectedLookupMetadata.bnc > 0)
      || (typeof selectedLookupMetadata.frq === 'number' && selectedLookupMetadata.frq > 0)
    )
  );
  const selectedLookupTags = useMemo(() => {
    if (!selectedLookupMetadata?.tag) return [] as string[];
    return selectedLookupMetadata.tag
      .split(/[,\s]+/)
      .map((token) => token.trim())
      .filter(Boolean);
  }, [selectedLookupMetadata?.tag]);

  const selectedWord = uiState.selectedWord;
  const isExplanationLoading = Boolean(
    selectedWord && (loading[selectedWord] || youdaoLoading[selectedWord])
  );
  const hasAttemptedLookup = useMemo(() => {
    if (!selectedWord) {
      return false;
    }
    return (
      Object.prototype.hasOwnProperty.call(loading, selectedWord) ||
      Object.prototype.hasOwnProperty.call(youdaoLoading, selectedWord)
    );
  }, [loading, selectedWord, youdaoLoading]);
  const hasExplanationContent = Boolean(
    selectedWord &&
    (translations[selectedWord] || explanations[selectedWord] || hasSelectedLookupMetadata)
  );
  const shouldShowNoExplanation = Boolean(
    selectedWord &&
    hasAttemptedLookup &&
    !isExplanationLoading &&
    !hasExplanationContent
  );

  // ===== 计算当前chunk索引 =====
  const currentChunkIndex = useMemo(() => {
    return group.chunks?.findIndex(
      (chunk) =>
        currentTime >= chunk.timestamp[0] && currentTime <= chunk.timestamp[1]
    ) ?? -1;
  }, [group.chunks, currentTime]);


  // ===== 删除本地hiddenWords状态，直接使用事件系统 =====


  // ===== Replay状态标志 =====
  const isReplayingRef = useRef(false);
  const shadowingReplayCompletedRef = useRef(false); // 跟踪shadowing replay是否已完成
  const replayAbortRef = useRef(false);
  const requestSkipHistory = useCallback((timeout: number = 2000) => {
    if (typeof window === 'undefined') return;
    (window as any).__PLAYER_SKIP_HISTORY_REPLAY__ = true;
    (window as any).__PLAYER_SKIP_HISTORY_REPLAY_TIMEOUT__ = Date.now() + timeout;
  }, []);
  
  const cleanupReplayCue = useCallback(() => {
    if (replayCueCleanupRef.current) {
      try {
        replayCueCleanupRef.current();
      } catch (error) {
        console.warn("Failed to cleanup replay cue:", error);
      }
      replayCueCleanupRef.current = null;
    }
  }, []);

  const releasePlaybackLock = useCallback(() => {
    const releaseLock = playbackLockReleasesRef.current.get(PLAYBACK_LOCK_KEY);
    if (releaseLock) {
      try {
        releaseLock();
      } catch (error) {
        console.warn('Failed to release playback lock:', error);
      }
      playbackLockReleasesRef.current.delete(PLAYBACK_LOCK_KEY);
    }
  }, []);

  const abortActiveReplay = useCallback(() => {
    let aborted = false;

    if (vocabularyReplayTimeoutRef.current) {
      clearTimeout(vocabularyReplayTimeoutRef.current);
      vocabularyReplayTimeoutRef.current = null;
      aborted = true;
    }

    if (shadowingTimeoutRef.current) {
      clearTimeout(shadowingTimeoutRef.current);
      shadowingTimeoutRef.current = null;
      aborted = true;
    }

    if (replayCueCleanupRef.current) {
      cleanupReplayCue();
      aborted = true;
    }

    if (isReplayingRef.current || shadowingReplayCompletedRef.current) {
      aborted = true;
    }

    if (aborted) {
      replayAbortRef.current = true;
    }

    isReplayingRef.current = false;
    shadowingReplayCompletedRef.current = false;
  }, [cleanupReplayCue]);
  
  // ===== 播放器状态同步辅助函数 =====
  const syncPlayerState = useCallback(async (action: 'pause' | 'play', reason?: string) => {
    console.log(`🔄 同步播放器状态: ${action}${reason ? ` (${reason})` : ''}`);
    
    // 优先使用全局播放器，回退到传入的player
    const activePlayer = globalPlayer || player;
    const isControlledPause =
      typeof reason === 'string' &&
      (reason.includes('replay') || reason.includes('dialog') || reason.includes('shadowing'));

    if (action === 'pause') {
      if (isControlledPause) {
        requestSkipHistory();
      }
      if (
        isControlledPause &&
        !playbackLockReleasesRef.current.has(PLAYBACK_LOCK_KEY) &&
        globalPlayer?.acquirePlaybackLock
      ) {
        playbackLockReleasesRef.current.set(
          PLAYBACK_LOCK_KEY,
          globalPlayer.acquirePlaybackLock(PLAYBACK_LOCK_KEY, { autoResume: false })
        );
      }

      if (!isControlledPause) {
        abortActiveReplay();
        releasePlaybackLock();
      }
    } else {
      if (typeof window !== 'undefined') {
        delete (window as any).__PLAYER_SKIP_HISTORY_REPLAY__;
        delete (window as any).__PLAYER_SKIP_HISTORY_REPLAY_TIMEOUT__;
      }
      replayAbortRef.current = false;
      releasePlaybackLock();
    }

    try {
      if (action === 'pause') {
        // 立即暂停播放器 - 确保暂停优先执行
        if (activePlayer?.pause) {
          activePlayer.pause();
          console.log('✅ 播放器已暂停');
        }
        
        // 然后更新UI状态
        if (setIsPlaying) {
          setIsPlaying(false);
        }
        
        // 验证暂停成功 - 移除 getState 检查，因为 player 接口不支持
        setTimeout(() => {
          // 不再检查播放状态，依赖暂停命令的执行
          console.log('✅ 播放器暂停命令已发送');
        }, 100);
      } else if (action === 'play') {
        // 在移动设备上，优先使用HTML5视频元素
        if (isMobile) {
          const videoElement = document.querySelector('video') as HTMLVideoElement;
          
          if (videoElement) {
            console.log('📱 移动设备 - 使用HTML5视频元素播放');
            try {
              const playResult = videoElement.play() as Promise<void> | void;
              
              if (playResult && typeof playResult === 'object' && 'then' in playResult) {
                await playResult;
                // 播放成功后更新UI状态
                if (setIsPlaying) {
                  setIsPlaying(true);
                }
              } else {
                if (setIsPlaying) {
                  setIsPlaying(true);
                }
              }
              return; // 成功后直接返回
            } catch (error) {
              console.error('❌ HTML5视频播放失败:', error);
              // 继续尝试使用player对象
            }
          }
        }
        
        // 桌面设备或移动设备fallback
        if (activePlayer?.play) {
          try {
            const playResult = activePlayer.play() as Promise<void> | void;
            
            // 检查是否返回了 Promise（不是 void）
            if (playResult && typeof playResult === 'object' && 'then' in playResult) {
              await playResult;
              // 播放成功后更新UI状态
              if (setIsPlaying) {
                setIsPlaying(true);
              }
            } else {
              // 同步播放或返回 void，直接更新状态
              if (setIsPlaying) {
                setIsPlaying(true);
              }
            }
          } catch (error) {
            console.error('❌ 播放失败:', error);
            if (setIsPlaying) {
              setIsPlaying(false);
            }
            throw error;
          }
        }
      }
    } catch (error) {
      console.error(`❌ 状态同步失败 (${action}):`, error);
    }
  }, [globalPlayer, player, setIsPlaying, isMobile, requestSkipHistory, abortActiveReplay, releasePlaybackLock]);

  useEffect(() => {
    return () => {
      cleanupReplayCue();
      playbackLockReleasesRef.current.forEach(release => {
        try {
          release();
        } catch (error) {
          console.warn('Failed to release playback lock on cleanup:', error);
        }
      });
      playbackLockReleasesRef.current.clear();
    };
  }, [cleanupReplayCue]);

  // ===== Dialog触发处理 =====
  const handleDialogTrigger = useCallback(async (event: DialogTriggerEvent) => {
    
    // 如果正在replay或replay已完成等待用户操作，忽略dialog触发
    if ((isReplayingRef.current && !replayAbortRef.current) || (shadowingReplayCompletedRef.current && !replayAbortRef.current)) {
      console.log(`🛡️ 阻止dialog触发 - replay: ${isReplayingRef.current}, completed: ${shadowingReplayCompletedRef.current}`);
      return;
    }
    
    setDialogState(prev => ({ ...prev, currentEvent: event }));
    
    switch (event.type) {
      case 'vocabulary_quiz':
        requestSkipHistory();
        setDialogState(prev => ({
          ...prev,
          showVocabDialog: true,
          savedVocabWords: event.payload.incompleteWords || [],
          completedWords: {}, // 重置completedWords状态，确保每个新的quiz都从空状态开始
          allWordsCompleted: false,
          activeWordIndex: 0,
          showQuizTranslation: false,
          revealedHiddenWords: {},
        }));
        
        await syncPlayerState('pause', 'vocabulary dialog');
        break;
        
      case 'shadowing_practice':
        requestSkipHistory();
        const sentences = event.payload.sentences || [{
          text: event.payload.chunkText,
          translation: event.payload.translation,
          timestamp: event.payload.chunkTimestamp || [currentTime, currentTime + 3]
        }];

        setShadowingState(prev => ({
          ...prev,
          currentSentence: sentences[0],
          showSentence: false, // 重置为隐藏状态
          showTranscription: false, // 重置转录显示
          transcriptionAccuracy: null, // 重置准确度
        }));

        setDialogState(prev => ({ ...prev, showShadowingDialog: true }));
        
        await syncPlayerState('pause', 'shadowing dialog');
        break;
        
      case 'chunk_completed':
        break;
    }
  }, [player, setIsPlaying, currentTime, requestSkipHistory, syncPlayerState]);

  // ===== 事件驱动系统 =====
  const {
    systemState, 
    markWordCompleted, 
    getAllHiddenWordsMap,
    getChunkHiddenWordsMap,
    getChunkVocabularyWords,
    findPhraseInText, // 直接从事件系统获取
    updateUserInteraction, // 用户交互状态更新
    receiveTimeUpdate,
    getStateMachine,
    forceRefresh
  } = useEventDrivenDialogSystem({
    chunks: group.chunks || [],
    player,
    practiceMode,
    practiceModeType,
    vocabularyTerms,
    isWordCheckerReady: isWordCheckerReady ?? false,
    onDialogTrigger: handleDialogTrigger
  });

  // ===== findPhraseInText 现在直接从事件驱动系统导出 =====

  // ===== 外部时间更新驱动事件系统 =====
  useEffect(() => {
    if (practiceMode && receiveTimeUpdate) {
      receiveTimeUpdate(currentTime);
    }
  }, [currentTime, practiceMode, receiveTimeUpdate]);

  // ===== 获取所有chunks的隐藏词汇状态（初始化时使用）=====
  const allChunksHiddenWords = useMemo(() => {

    if (!practiceMode || !group.chunks || group.chunks.length === 0) {
      return {};
    }

    const result = getAllHiddenWordsMap(group.chunks);
    
    
    return result;
  }, [practiceMode, group.chunks, getAllHiddenWordsMap, systemState.lastUpdate]);

  // ===== 获取当前chunk的隐藏词汇状态 =====
  const currentHiddenWords = useMemo(() => {
    if (!practiceMode || currentChunkIndex === -1 || !group.chunks?.[currentChunkIndex]) {
      return {};
    }
    
    const currentChunk = group.chunks[currentChunkIndex];
    return getChunkHiddenWordsMap(currentChunkIndex, currentChunk.text);
  }, [practiceMode, currentChunkIndex, group.chunks, getChunkHiddenWordsMap, systemState.lastUpdate]);

  // ===== 新系统：简化隐藏词汇状态管理，直接使用事件系统 =====
  const practiceHiddenWords = useMemo(() => {
    // 事件系统已包含用户交互状态，直接合并即可
    return { 
      ...allChunksHiddenWords,
      ...currentHiddenWords
    };
  }, [allChunksHiddenWords, currentHiddenWords]);

  // ===== Vocabulary 高亮 Map 构建 =====
  const normalizeVocabToken = useCallback((token: string): string => {
    return token.toLowerCase().replace(/[^\w]/g, '');
  }, []);

  const matchesWithVocabSuffix = useCallback((base: string, candidate: string): boolean => {
    if (base === candidate) return true;
    for (const suffix of VOCAB_SUFFIXES) {
      if (candidate === base + suffix) return true;
      if (base.endsWith('e') && suffix.startsWith('i') && candidate === base.slice(0, -1) + suffix) return true;
      if (base.endsWith('y') && candidate === base.slice(0, -1) + 'i' + suffix) return true;
      const lastChar = base.charAt(base.length - 1);
      if (/[bcdfgmnprt]/.test(lastChar) && candidate === base + lastChar + suffix) return true;
    }
    return false;
  }, []);

  const vocabularyHighlightMap = useMemo(() => {
    const map = new Map<string, VocabularyHighlightStyle>();
    
    // 如果没有 vocabulary terms，返回空 map
    if (!activeVocabularyTerms?.length && !staticVocabularyTerms?.length && !vocabularyTerms?.length) {
      return map;
    }

    // 收集所有要高亮的词汇
    const processed: Array<{ tokens: string[]; style: VocabularyHighlightStyle }> = [];

    // 处理 activeVocabularyTerms
    if (Array.isArray(activeVocabularyTerms)) {
      activeVocabularyTerms.forEach((entry) => {
        const term = entry?.term?.trim();
        if (!term) return;

        const tokens = term.split(/\s+/).map(normalizeVocabToken).filter(Boolean);
        if (tokens.length === 0) return;

        processed.push({
          tokens,
          style: {
            textClass: entry.textClass || DEFAULT_VOCAB_HIGHLIGHT_TEXT_CLASS,
            level: entry.level,
            fullTerm: term, // 🎯 完整的 vocabulary term
            // 🎯 包含 vocabulary 数据
            explanation: entry.explanation,
            context_description: entry.context_description,
            example: entry.example,
          },
        });
      });
    }

    // 处理 staticVocabularyTerms
    if (Array.isArray(staticVocabularyTerms)) {
      staticVocabularyTerms.forEach((entry) => {
        const term = entry?.term?.trim();
        if (!term) return;

        const tokens = term.split(/\s+/).map(normalizeVocabToken).filter(Boolean);
        if (tokens.length === 0) return;

        processed.push({
          tokens,
          style: {
            textClass: entry.textClass || DEFAULT_VOCAB_HIGHLIGHT_TEXT_CLASS,
            level: entry.level,
            fullTerm: term, // 🎯 完整的 vocabulary term
            // 🎯 包含 vocabulary 数据
            explanation: entry.explanation,
            context_description: entry.context_description,
            example: entry.example,
          },
        });
      });
    }

    // 处理普通的 vocabularyTerms
    if (Array.isArray(vocabularyTerms) && !practiceMode) {
      vocabularyTerms.forEach((rawTerm) => {
        const term = rawTerm?.trim();
        if (!term) return;

        const tokens = term.split(/\s+/).map(normalizeVocabToken).filter(Boolean);
        if (tokens.length === 0) return;

        processed.push({
          tokens,
          style: {
            textClass: DEFAULT_VOCAB_HIGHLIGHT_TEXT_CLASS,
          },
        });
      });
    }

    if (processed.length === 0) return map;

    // 遍历所有 chunks 的文本，构建高亮 map
    group.chunks?.forEach((chunk, chunkIndex) => {
      const words = chunk.text.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/g) || [];
      const normalizedWords = words.map((word, wordIndex) => ({
        id: `${chunkIndex}-${wordIndex}-${word}`,
        word,
        normalized: normalizeVocabToken(word),
      }));

      processed.forEach(({ tokens, style }) => {
        if (tokens.length === 0) return;

        for (let startIndex = 0; startIndex <= normalizedWords.length - tokens.length; startIndex += 1) {
          let match = true;

          for (let offset = 0; offset < tokens.length; offset += 1) {
            const wordInfo = normalizedWords[startIndex + offset];
            if (!wordInfo || !matchesWithVocabSuffix(tokens[offset]!, wordInfo.normalized)) {
              match = false;
              break;
            }
          }

          if (match) {
            for (let offset = 0; offset < tokens.length; offset += 1) {
              const wordInfo = normalizedWords[startIndex + offset];
              if (wordInfo && !map.has(wordInfo.id)) {
                map.set(wordInfo.id, {
                  ...style,
                  showBadge: offset === tokens.length - 1 && Boolean(style.level),
                });
              }
            }
          }
        }
      });
    });

    return map;
  }, [activeVocabularyTerms, staticVocabularyTerms, vocabularyTerms, practiceMode, group.chunks, normalizeVocabToken, matchesWithVocabSuffix]);

  // ===== 新系统：统一文本渲染 =====
  const { renderText, clearCache } = useOptimizedTextRenderer({
    wordHighlightEnabled,
    practiceMode,
    vocabularyTerms,
    practiceModeType: practiceModeType === 'shadowing' ? 'vocabulary' : practiceModeType,
    isWordCheckerReady: isWordCheckerReady ?? false,
    userSettings,
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
    selectedWord: uiState.selectedWord,
    showWordDrawer: uiState.showWordDrawer,
    setSelectedWord,
    setShowWordDrawer,
    speakerRef,
    episodeId,
    episodeTitle,
    episodeThumbnail,
    hiddenWords: practiceHiddenWords,
    setHiddenWords: useCallback((updater: React.SetStateAction<Record<string, boolean>>) => {
      // 适配器函数：将React的setState模式转换为我们的updateUserInteraction
      if (typeof updater === 'function') {
        const currentState = practiceHiddenWords;
        const newState = updater(currentState);
        // 只更新变化的部分
        Object.keys(newState).forEach(key => {
          if (newState[key] !== currentState[key]) {
            const nextValue = newState[key];
            if (typeof nextValue === 'boolean') {
              updateUserInteraction(key, nextValue);
            }
          }
        });
      } else {
        // 直接设置新状态
        Object.keys(updater).forEach(key => {
          const nextValue = updater[key];
          if (typeof nextValue === 'boolean') {
            updateUserInteraction(key, nextValue);
          }
        });
      }
    }, [practiceHiddenWords, updateUserInteraction]),
    findPhraseInText: findPhraseInText,
    // 添加有道翻译支持
    youdaoTranslations: translations,
    youdaoLoading: youdaoLoading,
    youdaoGetTranslation: getTranslation,
    youdaoIsInitialized: isInitialized,
  });

  useEffect(() => {
    if (!practiceMode) {
      return;
    }

    clearCache();
    forceRefresh();
    forcePracticeRender((value) => value + 1);
  }, [practiceMode, practiceModeType, isWordCheckerReady, vocabularyTerms, clearCache, forceRefresh]);

  // ===== Vocabulary 高亮渲染函数 =====
  const renderTextWithVocabHighlight = useCallback((text: string, chunkIndex: number): React.ReactNode => {
    const normalizedText = text.replace(/\u00a0/g, ' ');
    
    // 如果没有 vocabulary 高亮 map 或者在 practice mode，使用原有的 renderText
    if (vocabularyHighlightMap.size === 0 || practiceMode) {
      return renderText(normalizedText, chunkIndex);
    }

    // 分词并应用高亮
    const tokens = normalizedText.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b|\d+%?|\s+|[^\w\s]/g) || [];
    let wordIndex = 0;
    
    const elements = tokens.map((token, tokenIndex) => {
      const isValidWord = /^[a-zA-Z]+(?:'[a-zA-Z]+)?$/.test(token);
      
      if (!isValidWord) {
        return <span key={tokenIndex}>{token}</span>;
      }

      // 检查这个词是否需要高亮
      const wordId = `${chunkIndex}-${wordIndex}-${token}`;
      const highlightStyle = vocabularyHighlightMap.get(wordId);
      wordIndex++;

      if (highlightStyle) {
        // 🎯 只使用文字颜色高亮，与 TimeDrivenTranscript 保持一致
        const displayLevel = highlightStyle.level;
        const shouldShowLevelBadge = Boolean(highlightStyle.showBadge && displayLevel);
        
        // 🎯 使用完整的 vocabulary term（如 "planning their time off"），而不是单个词
        const displayWord = highlightStyle.fullTerm || token;

        // 🎯 如果有预填充的 vocabulary 数据，创建包含它的 explanations 对象
        // 这样 WordHoverCard 会直接显示 explanation，而不调用翻译 API
        let mergedExplanations = explanations;
        if (highlightStyle.explanation || highlightStyle.context_description || highlightStyle.example) {
          const lang = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
          
          // 解析 explanation：可能是 string 或 Record<string, string>
          let translationText = '';
          const rawExplanation = highlightStyle.explanation;
          if (typeof rawExplanation === 'string') {
            translationText = rawExplanation;
          } else if (typeof rawExplanation === 'object' && rawExplanation !== null) {
            translationText = rawExplanation[lang] || rawExplanation['zh'] || Object.values(rawExplanation)[0] || '';
          }
          
          // 解析例句
          let exampleText = '';
          const rawExample = highlightStyle.example;
          if (rawExample?.sentence) {
            exampleText = rawExample.sentence;
          }
          
          // 构建预填充数据 - 🎯 使用 token 作为 key，但包含完整的 term 信息
          if (translationText || exampleText || highlightStyle.context_description || displayWord) {
            mergedExplanations = {
              ...explanations,
              [token]: {
                translation: translationText,
                example: exampleText,
                contextDescription: highlightStyle.context_description,
                // 🎯 完整的 vocabulary term（用于在 hover card 标题中显示）
                fullTerm: displayWord !== token ? displayWord : undefined,
                // 标记这是来自 vocabulary 的预填充数据
                source: 'vocabulary' as const,
              },
            };
          }
        }

        return (
          <span
            key={tokenIndex}
            className={cn(
              "vocab-highlight-word relative inline-block font-semibold transition-colors duration-200",
              highlightStyle.textClass
            )}
          >
            {/* 等级徽章 - 与 TimeDrivenTranscript 一致 */}
            {shouldShowLevelBadge && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute -top-[5px] right-0 translate-x-1/2 rounded-full bg-neutral-900/70 px-[2px] py-px text-[0.45rem] uppercase tracking-wide text-white shadow-sm dark:bg-white/90 dark:text-neutral-900 z-[1]"
              >
                {displayLevel}
              </span>
            )}
            <WordHoverCard
              word={token}
              index={tokenIndex}
              variant="subtitle"
              isSearchMatch={false}
              isWordCheckerReady={isWordCheckerReady}
              userSettings={userSettings}
              explanations={mergedExplanations}
              loading={loading}
              savedWords={savedWords}
              savingWord={savingWord}
              onGetExplanation={getExplanation}
              onSaveCard={saveCard}
              isCardSaved={isCardSaved}
              selectedWord={uiState.selectedWord}
              showWordDrawer={uiState.showWordDrawer}
              setSelectedWord={setSelectedWord}
              setShowWordDrawer={setShowWordDrawer}
              speakerRef={speakerRef}
              youdaoTranslations={translations}
              youdaoLoading={youdaoLoading}
              youdaoGetTranslation={getTranslation}
              youdaoIsInitialized={isInitialized}
              episodeId={episodeId}
              episodeTitle={episodeTitle}
              episodeThumbnail={episodeThumbnail}
              contextText={normalizedText}
              enableMobileDrawer={false}
            />
          </span>
        );
      }

      // 普通词汇，直接渲染为文本（不显示 hover card）
      return <span key={tokenIndex}>{token}</span>;
    });

    return <span>{elements}</span>;
  }, [
    vocabularyHighlightMap,
    practiceMode,
    renderText,
    isWordCheckerReady,
    userSettings,
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
    uiState.selectedWord,
    uiState.showWordDrawer,
    setSelectedWord,
    setShowWordDrawer,
    speakerRef,
    translations,
    youdaoLoading,
    getTranslation,
    isInitialized,
    episodeId,
    episodeTitle,
    episodeThumbnail,
  ]);

  // ===== 新系统：dialog渲染，支持隐藏单词 =====
  const handleRevealHiddenWord = useCallback((wordKey: string) => {
    setDialogState(prev => {
      if (prev.revealedHiddenWords[wordKey]) {
        return prev;
      }

      return {
        ...prev,
        revealedHiddenWords: {
          ...prev.revealedHiddenWords,
          [wordKey]: true,
        },
      };
    });
  }, []);

  const hoverCardSharedProps = useMemo(() => ({
    isSearchMatch: false,
    isWordCheckerReady: isWordCheckerReady ?? false,
    userSettings,
    explanations,
    loading,
    savedWords,
    savingWord,
    onGetExplanation: getExplanation,
    onSaveCard: saveCard,
    isCardSaved,
    selectedWord: uiState.selectedWord,
    showWordDrawer: uiState.showWordDrawer,
    setSelectedWord,
    setShowWordDrawer,
    speakerRef,
    youdaoTranslations: translations,
    youdaoLoading,
    youdaoGetTranslation: getTranslation,
    youdaoIsInitialized: isInitialized,
    episodeId,
    episodeTitle,
    episodeThumbnail,
  }), [
    isWordCheckerReady,
    userSettings,
    explanations,
    loading,
    savedWords,
    savingWord,
    getExplanation,
    saveCard,
    isCardSaved,
    uiState.selectedWord,
    uiState.showWordDrawer,
    setSelectedWord,
    setShowWordDrawer,
    speakerRef,
    translations,
    youdaoLoading,
    getTranslation,
    isInitialized,
    episodeId,
    episodeTitle,
    episodeThumbnail,
  ]);

  const renderSentenceWithHiddenWords = useCallback((sentence: string, words: any[]) => {
    if (!dialogState.showVocabDialog || !words.length) {
      return <span>{sentence}</span>;
    }
    
    if (!dialogState.currentEvent) {
      return <span>{sentence}</span>;
    }
    
    const chunkIndex = dialogState.currentEvent.chunkIndex;
    const chunkText = dialogState.currentEvent.payload?.chunkText || sentence;
    const vocabularyWords = getChunkVocabularyWords(chunkIndex, chunkText);
    
    if (!vocabularyWords.length) {
      return <span>{chunkText}</span>;
    }

    const sortedWords = [...vocabularyWords].sort((a, b) => a.position.start - b.position.start);
    const nodes: React.ReactNode[] = [];
    let cursor = 0;

    sortedWords.forEach((vocabWord, index) => {
      const start = Math.max(0, vocabWord.position.start);
      const end = Math.min(chunkText.length, vocabWord.position.end);

      if (start < cursor || end <= start) {
        return;
      }

      if (start > cursor) {
        nodes.push(
          <span key={`text-${index}-${cursor}`}>{chunkText.slice(cursor, start)}</span>
        );
      }

      const wordText = chunkText.slice(start, end);
      const uniqueKey = `${chunkIndex}-${start}-${end}`;
      const isCompleted = dialogState.completedWords[vocabWord.original] || false;
      const isRevealed = dialogState.revealedHiddenWords[uniqueKey];
      const shouldHighlight = isCompleted || isRevealed;

      if (shouldHighlight) {
        nodes.push(
          <span
            key={`word-${index}-${uniqueKey}`}
            className={cn(
              "inline-flex font-semibold transition-colors",
              isCompleted ? "opacity-100" : "opacity-90"
            )}
          >
            <WordHoverCard
              word={wordText}
              index={`${uniqueKey}`}
              contextText={chunkText}
              variant="subtitle"
              isHighlighted={true}
              {...hoverCardSharedProps}
              enableMobileDrawer={false}
            />
          </span>
        );
      } else {
        const placeholder = "_".repeat(Math.max(wordText.length, 3));
        nodes.push(
          <span
            key={`hidden-${index}-${uniqueKey}`}
            className="inline-flex cursor-pointer select-none items-center justify-center rounded-sm px-1 font-mono text-base tracking-widest text-muted-foreground/70 hover:bg-muted/40 focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            onClick={() => handleRevealHiddenWord(uniqueKey)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleRevealHiddenWord(uniqueKey);
              }
            }}
            aria-label="Reveal hidden word"
            title="Click to reveal word"
          >
            {placeholder}
          </span>
        );
      }

      cursor = end;
    });

    if (cursor < chunkText.length) {
      nodes.push(
        <span key={`text-end-${cursor}`}>{chunkText.slice(cursor)}</span>
      );
    }
    
    return <span className="whitespace-pre-wrap">{nodes}</span>;
  }, [
    dialogState.showVocabDialog,
    dialogState.currentEvent,
    dialogState.completedWords,
    dialogState.revealedHiddenWords,
    getChunkVocabularyWords,
    handleRevealHiddenWord,
    hoverCardSharedProps,
  ]);

  const handleToggleQuizTranslation = useCallback(() => {
    setDialogState(prev => ({
      ...prev,
      showQuizTranslation: !prev.showQuizTranslation,
    }));
  }, []);

  // ===== 词汇翻译获取 =====
  const getWordTranslation = useCallback(
    (word: { original: string; phrase: string }) => {
      const wordKey = word.original;

      const explanation = explanations[wordKey];
      if (explanation?.translation && explanation.translation !== wordKey) {
        return explanation.translation;
      }

      if (practiceModeType === "toefl" && isWordCheckerReady) {
        try {
          const dictTranslation = (
            window as any
          ).vocabularyLevelChecker?.getTranslation?.(wordKey);
          if (
            dictTranslation &&
            Array.isArray(dictTranslation) &&
            dictTranslation.length > 0
          ) {
            return dictTranslation[0];
          }
        } catch {
          // 静默处理错误
        }
      }

      if (word.phrase && word.phrase !== wordKey.toLowerCase()) {
        return word.phrase;
      }

      return undefined;
    },
    [explanations, practiceModeType, isWordCheckerReady]
  );

  // ===== 事件处理函数 =====
  const jumpToTime = useCallback(
    (timeInSeconds: number) => {
      // 优先使用全局播放器
      if (globalPlayer && globalPlayer.seekTo) {
        console.log('🎯 Using global player to seek to:', timeInSeconds);
        globalPlayer.seekTo(timeInSeconds);
        return;
      }
      
      // 回退到本地播放器
      if (player) {
        console.log('🎯 Using local player to seek to:', timeInSeconds);
        player.seekTo(timeInSeconds);
        return;
      }
      
      // 最后回退到onSeek回调
      if (onSeek) {
        console.log('🎯 Using onSeek callback to seek to:', timeInSeconds);
        onSeek(timeInSeconds);
      }
    },
    [globalPlayer, player, onSeek]
  );

  // const handleButtonClick = useCallback(
  //   (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
  //     e.stopPropagation();
  //     
  //     // Get the position of the button's parent chunk element
  //     const chunkElement = e.currentTarget.closest('span');
  //     if (chunkElement) {
  //       const rect = chunkElement.getBoundingClientRect();
  //       const position = {
  //         x: rect.left,
  //         y: rect.bottom // 使用视口坐标，因为FloatingToolbar用的是fixed定位
  //       };
  //       setToolbarPosition(position);
  //     }
  //     
  //     setActiveChunk(index);
  //     setShowButton(false);
  //     setShowToolbar(true);
  //     
  //     // 新增：同时设置高亮chunk
  //     setHighlightedChunkIndex(index);
  //   },
  //   [setActiveChunk, setShowButton, setShowToolbar, setToolbarPosition, setHighlightedChunkIndex]
  // );

  // Stream Object state for analysis - 使用懒加载模式
  const [, setIsAnalysisActive] = useState(false);
  const [analysisObject, setAnalysisObject] = useState<any>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // 手动实现分析请求，避免 useObject 的自动轮询
  const submitAnalysis = useCallback(async (data: { sentence: string; language: string }) => {
    try {
      // 取消之前的请求
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const response = await fetch('/api/analyze-sentence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Analysis failed: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        
        // 尝试解析累积的 JSON
        try {
          // 查找完整的 JSON 对象
          const lines = buffer.split('\n').filter(line => line.trim());
          for (const line of lines) {
            if (line.startsWith('{') || line.startsWith('[')) {
              try {
                const parsed = JSON.parse(line);
                setAnalysisObject(parsed);
              } catch {
                // 不是完整的 JSON，继续累积
              }
            }
          }
        } catch {
          // 继续读取
        }
      }

      // 最终解析
      try {
        // 尝试提取最后的完整 JSON
        const jsonMatch = buffer.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const finalResult = JSON.parse(jsonMatch[0]);
          setAnalysisObject(finalResult);
        }
      } catch (e) {
        console.error('Failed to parse final result:', e);
      }

      setIsAnalyzing(false);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Analysis error:', error);
        setIsAnalyzing(false);
        setIsAnalysisActive(false);
      }
    }
  }, [setIsAnalyzing]);

  const stopAnalysis = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  const handleAnalyze = useCallback(
    async (text: string) => {
      try {
        setShowToolbar(false);
        setShowAnalysis(true);
        setIsAnalyzing(true);
        setIsAnalysisActive(true);
        setAnalysisObject(null); // 重置分析结果

        const currentLanguage = userSettings?.languageSetting || "chinese";

        // Submit the analysis request
        submitAnalysis({
          sentence: text,
          language: currentLanguage,
        });

      } catch (error) {
        console.error("Analysis error:", error);
        setIsAnalyzing(false);
        setIsAnalysisActive(false);
      }
    },
    [
      userSettings,
      setShowToolbar,
      setShowAnalysis,  
      setIsAnalyzing,
      submitAnalysis,
    ]
  );

  const handleCloseAnalysis = useCallback(() => {
    setShowAnalysis(false);
    setActiveChunk(null);
    setHoveredChunk(null);
    setShowButton(true);
    setIsAnalysisActive(false);
    setAnalysisObject(null);
    stopAnalysis();
  }, [
    setShowAnalysis,
    setActiveChunk,
    setHoveredChunk,
    setShowButton,
    stopAnalysis,
  ]);

  // const handlePlay = useCallback(() => {
  //   const selectedChunk = group.chunks?.[uiState.activeChunk || 0];
  //   if (selectedChunk && player) {
  //     player.seekTo(selectedChunk.timestamp[0]);
  //     try {
  //       player.play();
  //     } catch (error) {
  //       console.error("Error playing video:", error);
  //     }
  //   } else if (selectedChunk && onSeek) {
  //     onSeek(selectedChunk.timestamp[0]);
  //   }
  // }, [group.chunks, uiState.activeChunk, player, onSeek]);

  // const handlePlaySpeed = useCallback(
  //   (speed: number) => {
  //     if (uiState.activeChunk !== null && player && group.chunks) {
  //       const selectedChunk = group.chunks[uiState.activeChunk];
  //       player.seekTo(selectedChunk.timestamp[0]);
  //       player.setPlaybackRate(speed);

  //       const playbackInterval = setInterval(() => {
  //         if (player.getCurrentTime() >= selectedChunk.timestamp[1]) {
  //           player.pause();
  //           player.setPlaybackRate(1.0);
  //           clearInterval(playbackInterval);
  //         }
  //       }, 100);

  //       try {
  //         player.play();
  //       } catch (error) {
  //         console.error("Error playing video:", error);
  //       }
  //     }
  //   },
  //   [group.chunks, uiState.activeChunk, player]
  // );

  // ===== 词汇对话框处理 =====
  const handleVocabWordCompleted = useCallback((word: string) => {
    if (dialogState.currentEvent) {
      const updatedState = markWordCompleted(dialogState.currentEvent.chunkId, word);
      
      // 更新本地完成状态
      setDialogState(prev => ({
        ...prev,
        completedWords: {
          ...prev.completedWords,
          [word]: true
        }
      }));

          // 更新全局隐藏词汇状态 - 仅针对当前chunk
    if (group.chunks?.[dialogState.currentEvent.chunkIndex]) {
      const chunkText = group.chunks[dialogState.currentEvent.chunkIndex].text;
      const chunkIndex = dialogState.currentEvent.chunkIndex;
      const vocabularyWords = getChunkVocabularyWords(chunkIndex, chunkText);
      
      // 找到匹配的词汇对象，更新所有格式的key（仅针对当前chunk）
      vocabularyWords.forEach(vocabWord => {
        if (vocabWord.original.toLowerCase() === word.toLowerCase()) {
          // 更新所有格式的key以确保兼容性
          const uniqueKey = `${vocabWord.phrase.toLowerCase()}-${vocabWord.position.start}-${vocabWord.position.end}`;
          const traditionalKey = `${vocabWord.original.toLowerCase()}-${chunkIndex}`;
          const simpleKey = vocabWord.original.toLowerCase();
          
          updateUserInteraction(uniqueKey, true); // true表示显示（不隐藏）
          updateUserInteraction(traditionalKey, true);
          updateUserInteraction(simpleKey, false); // 将简单键隐藏，避免跨chunk泄露
        }
      });
      
      // 🔥 关键修复：移除cross-chunk同步逻辑
      // 每个chunk的quiz应该是独立的，用户需要在每个chunk中分别练习相同的单词
      // 这样可以提供更好的学习体验和重复练习机会
    }

      // 检查是否所有词汇都已完成
      if (updatedState && updatedState.practiceData.completionPercentage >= 100) {
        setTimeout(() => {
          setDialogState(prev => ({ ...prev, allWordsCompleted: true }));
          
          // 如果正在replay，只关闭dialog，让replay播放继续
          if (!replayAbortRef.current && isReplayingRef.current && (vocabularyReplayTimeoutRef.current || replayCueCleanupRef.current)) {
            // 不要清除replay标志，让replay正常完成
            setTimeout(() => {
              setDialogState(prev => ({
                ...prev,
                showVocabDialog: false,
                currentEvent: null,
                savedVocabWords: [],
                showQuizTranslation: false,
                revealedHiddenWords: {},
              }));
            }, 1500);
          } else {
            // 非replay模式，正常处理
            setTimeout(() => {
              handleContinuePlayback();
            }, 1500);
          }
        }, 500);
      }
    }
  }, [dialogState.currentEvent, markWordCompleted, updateUserInteraction, group.chunks, getChunkVocabularyWords]);

  const handleContinuePlayback = useCallback(() => {
    const currentEvent = dialogState.currentEvent;
    const stateMachine = getStateMachine();

    if (currentEvent && stateMachine) {
      stateMachine.suppressChunkDialog(currentEvent.chunkId, { markSkipped: true });
    }

    setDialogState(prev => ({
      ...prev,
      showVocabDialog: false,
      currentEvent: null,
      savedVocabWords: [],
      showQuizTranslation: false,
      revealedHiddenWords: {},
    }));

    // 如果正在replay，不要干扰replay过程
    if (isReplayingRef.current && !replayAbortRef.current) {
      return;
    }

    setTimeout(async () => {
      // console.log('🎬 handleContinuePlayback - 准备恢复播放');
      
      // 使用统一的状态同步方法
      try {
        await syncPlayerState('play', 'vocabulary continue');
        // console.log('✅ handleContinuePlayback - 播放状态已同步');
      } catch (error) {
        console.error("❌ handleContinuePlayback - Error resuming playback:", error);
      }
    }, 100);
  }, [dialogState.currentEvent, getStateMachine, syncPlayerState]);

  const handleReplay = useCallback(() => {
    // 先清理任何现有的timeout或cue
    if (vocabularyReplayTimeoutRef.current) {
      clearTimeout(vocabularyReplayTimeoutRef.current);
      vocabularyReplayTimeoutRef.current = null;
    }
    cleanupReplayCue();

    // 设置replay标志，防止事件系统干扰
    replayAbortRef.current = false;
    isReplayingRef.current = true;

    setDialogState(prev => ({ ...prev, showVocabDialog: false, showQuizTranslation: false, revealedHiddenWords: {} }));

    // 增加延迟，确保dialog完全关闭后再开始播放
    setTimeout(async () => {
      const activePlayer = globalPlayer || player;
      
      if (activePlayer && dialogState.currentEvent) {
        const chunkIndex = dialogState.currentEvent.chunkIndex;
        const currentChunk = group.chunks?.[chunkIndex];

        if (currentChunk) {
          const timestamps = Array.isArray(currentChunk.timestamp)
            ? currentChunk.timestamp
            : [0, 0];
          const startTime = Number(timestamps[0]);
          const endTime = Number(timestamps[1]);

          releasePlaybackLock();

          if (Number.isFinite(startTime)) {
            activePlayer.seekTo(startTime);
          }
          
          await new Promise(resolve => setTimeout(resolve, 100));
          
          try {
            await syncPlayerState('play', 'vocabulary replay start');
          } catch (syncError) {
            console.error('Failed to sync play state before replay:', syncError);
          }
          
          try {
            if (activePlayer.play) {
              const playResult = activePlayer.play() as Promise<void> | void;
              
              if (playResult && typeof playResult === 'object' && 'then' in playResult) {
                try {
                  await playResult;
                  if (setIsPlaying) {
                    setIsPlaying(true);
                  }
                } catch (playError) {
                  console.error("播放失败:", playError);
                  try {
                    await syncPlayerState('play', 'vocabulary replay retry');
                  } catch (retryError) {
                    console.error("重试播放失败:", retryError);
                    if (setIsPlaying) {
                      setIsPlaying(false);
                    }
                    isReplayingRef.current = false;
                    return;
                  }
                }
              } else if (setIsPlaying) {
                setIsPlaying(true);
              }
            } else {
              try {
                await syncPlayerState('play', 'vocabulary replay sync');
              } catch (playError) {
                console.error("播放失败:", playError);
                isReplayingRef.current = false;
                return;
              }
            }

            const scheduleFallbackPause = () => {
              if (vocabularyReplayTimeoutRef.current) {
                return;
              }

              const chunkDuration = Number.isFinite(endTime) && Number.isFinite(startTime)
                ? endTime - startTime
                : NaN;
              const pauseDelay = Number.isFinite(chunkDuration)
                ? Math.max(100, (chunkDuration - 0.05) * 1000)
                : 500;

              vocabularyReplayTimeoutRef.current = setTimeout(() => {
                if (!isReplayingRef.current) {
                  vocabularyReplayTimeoutRef.current = null;
                  return;
                }

                const currentState = dialogStateRef.current;
                const shouldContinue =
                  currentState.allWordsCompleted ||
                  currentState.savedVocabWords.length === 0;

                if (shouldContinue) {
                  isReplayingRef.current = false;
                  vocabularyReplayTimeoutRef.current = null;
                  
                  syncPlayerState('play', 'vocabulary replay complete').catch(error => {
                    console.error("Error continuing playback after replay:", error);
                  });
                } else {
                  syncPlayerState('pause', 'vocabulary replay incomplete').catch(error => {
                    console.error("Error pausing after replay:", error);
                  });
                  
                  vocabularyReplayTimeoutRef.current = null;
                  isReplayingRef.current = false;
                  
                  setTimeout(() => {
                    setDialogState(prev => ({ ...prev, showVocabDialog: true }));
                  }, 20);
                }
              }, pauseDelay);
            };

            let handledByCue = false;

            if (
              mediaCueRegistry.hasCueSupport() &&
              mediaCueRegistry.getActiveMediaElement() &&
              Number.isFinite(startTime) &&
              Number.isFinite(endTime) &&
              endTime > startTime
            ) {
              const sourceId = `vocab-replay-${chunkIndex}-${Date.now()}`;
              const descriptor = {
                id: sourceId,
                start: startTime,
                end: endTime,
                data: { chunkIndex },
              };

              replayCueCleanupRef.current = mediaCueRegistry.registerCueSet(
                sourceId,
                [descriptor],
                {
                  onExit: () => {
                    if (!isReplayingRef.current) {
                      return;
                    }

                    if (vocabularyReplayTimeoutRef.current) {
                      clearTimeout(vocabularyReplayTimeoutRef.current);
                      vocabularyReplayTimeoutRef.current = null;
                    }

                    cleanupReplayCue();

                    const currentState = dialogStateRef.current;
                    const shouldContinue =
                      currentState.allWordsCompleted ||
                      currentState.savedVocabWords.length === 0;

                    if (shouldContinue) {
                      isReplayingRef.current = false;
                      syncPlayerState('play', 'vocabulary replay cue complete').catch(error => {
                        console.error("Error continuing playback after replay cue:", error);
                      });
                    } else {
                      syncPlayerState('pause', 'vocabulary replay cue incomplete').catch(error => {
                        console.error("Error pausing after replay cue:", error);
                      });
                      isReplayingRef.current = false;
                      setTimeout(() => {
                        setDialogState(prev => ({ ...prev, showVocabDialog: true }));
                      }, 20);
                    }
                  },
                },
                {
                  onAttachChange(attached) {
                    if (!attached && replayCueCleanupRef.current) {
                      cleanupReplayCue();
                      scheduleFallbackPause();
                    }
                  },
                }
              );

              handledByCue = Boolean(replayCueCleanupRef.current);
            }

            if (!handledByCue) {
              scheduleFallbackPause();
            }
          } catch (error) {
            console.error("Error in vocabulary replay:", error);
            syncPlayerState('pause', 'vocabulary replay outer error').catch(e => {
              console.error("Failed to pause on outer error:", e);
            });
            cleanupReplayCue();
            isReplayingRef.current = false;
          }
        }
      }
    }, 300);
  }, [syncPlayerState, globalPlayer, player, dialogState, group.chunks, setIsPlaying, cleanupReplayCue, releasePlaybackLock]);

  const handleVocabDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setDialogState(prev => ({ ...prev, showVocabDialog: true }));
        return;
      }

      const replayActive = isReplayingRef.current && !replayAbortRef.current;

      if (!replayActive) {
        abortActiveReplay();
      }

      setDialogState(prev => ({
        ...prev,
        showVocabDialog: false,
        showQuizTranslation: false,
        revealedHiddenWords: {},
      }));

      releasePlaybackLock();
    },
    [abortActiveReplay, releasePlaybackLock]
  );

  const handleShadowingDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setDialogState(prev => ({ ...prev, showShadowingDialog: true }));
        return;
      }

      const replayActive = isReplayingRef.current && !replayAbortRef.current;

      if (!replayActive) {
        abortActiveReplay();
      }

      setDialogState(prev => ({
        ...prev,
        showShadowingDialog: false,
      }));

      releasePlaybackLock();
    },
    [abortActiveReplay, releasePlaybackLock]
  );

  // ===== 阴影练习处理 =====
  const handleShadowingReplay = useCallback((speed: number = shadowingState.playbackSpeed) => {
    // 优先使用全局播放器，回退到传入的player
    const activePlayer = globalPlayer || player;
    
    if (activePlayer && shadowingState.currentSentence) {
      // 清理现有的timeout
      if (shadowingTimeoutRef.current) {
        clearTimeout(shadowingTimeoutRef.current);
        shadowingTimeoutRef.current = null;
      }

      // 重置replay完成状态
      shadowingReplayCompletedRef.current = false;
      // 设置replay标志，防止事件系统干扰
      replayAbortRef.current = false;
      isReplayingRef.current = true;

      activePlayer.seekTo(shadowingState.currentSentence.timestamp[0]);
      
      // 全局播放器使用 setPlaybackSpeed，本地播放器使用 setPlaybackRate
      if (activePlayer === globalPlayer && activePlayer.setPlaybackSpeed) {
        activePlayer.setPlaybackSpeed(speed);
      } else if (activePlayer.setPlaybackRate) {
        activePlayer.setPlaybackRate(speed);
      }

      // 等待seekTo完成
      setTimeout(async () => {
        try {
          // 优先使用全局播放器或传入的player
          if (activePlayer && activePlayer.play) {
            try {
              const playResult = activePlayer.play() as Promise<void> | void;
              
              if (playResult && typeof playResult === 'object' && 'then' in playResult) {
                await playResult;
                // 播放成功后，再同步外部状态
                if (setIsPlaying) {
                  setIsPlaying(true);
                }
              } else {
                // 同步播放，直接更新状态
                if (setIsPlaying) {
                  setIsPlaying(true);
                }
              }
            } catch (playError) {
              console.error("播放器播放失败:", playError);
              
              // 如果播放失败，尝试使用 syncPlayerState 
              try {
                await syncPlayerState('play', 'shadowing replay retry');
              } catch (retryError) {
                console.error("重试播放失败:", retryError);
                await syncPlayerState('pause', 'play failed');
                isReplayingRef.current = false;
                return;
              }
            }
          } else {
            // 如果没有可用的播放器，使用 syncPlayerState
            try {
              await syncPlayerState('play', 'shadowing replay no player');
            } catch (playError) {
              console.error("播放失败:", playError);
              isReplayingRef.current = false;
              return;
            }
          }

          const sentenceDuration =
            shadowingState.currentSentence.timestamp[1] - shadowingState.currentSentence.timestamp[0];
          // 使用与Vocabulary模式相同的时间计算逻辑
          const pauseDelay = Math.max(100, (sentenceDuration - 0.05) * 1000 / speed);

          shadowingTimeoutRef.current = setTimeout(() => {
            // 恢复播放速度为 1.0
            if (activePlayer === globalPlayer && activePlayer.setPlaybackSpeed) {
              activePlayer.setPlaybackSpeed(1.0);
            } else if (activePlayer.setPlaybackRate) {
              activePlayer.setPlaybackRate(1.0);
            }
            syncPlayerState('pause', 'shadowing replay complete').catch(error => {
              console.error("Error pausing after shadowing replay:", error);
            });
            shadowingTimeoutRef.current = null;
            
            // 标记replay已完成，但保持保护状态
            shadowingReplayCompletedRef.current = true;
            // console.log('🎯 Shadowing replay完成，保持保护状态直到用户点击continue');
          }, pauseDelay);
        } catch (error) {
          console.error("Error in shadowing replay:", error);
          syncPlayerState('pause', 'shadowing replay error').catch(e => {
            console.error("Failed to pause on shadowing error:", e);
          });
          // 确保在错误情况下也清除replay标志
          isReplayingRef.current = false;
        }
      }, 100);
    }
  }, [syncPlayerState, globalPlayer, player, shadowingState.currentSentence, shadowingState.playbackSpeed, setIsPlaying]);

  const handleShadowingContinue = useCallback(() => {
    // console.log(`🎯 Shadowing Continue开始 - replay completed: ${shadowingReplayCompletedRef.current}`);
    
    // 标记当前chunk为完成状态，防止重复触发
    if (dialogState.currentEvent) {
      const chunkId = dialogState.currentEvent.chunkId;
      const chunkIndex = dialogState.currentEvent.chunkIndex;
      
      // console.log(`🎯 Shadowing Continue - 标记chunk ${chunkIndex} 为完成`);
      
      // 获取状态机并标记chunk为完成
      const stateMachine = getStateMachine();
      if (stateMachine) {
        stateMachine.updateChunkStatus(chunkId, 'completed', {
          reason: 'shadowing_continue',
          timestamp: Date.now()
        });
      }
      
      // 添加当前句子到已处理列表
      if (shadowingState.currentSentence) {
        const sentenceKey = `${shadowingState.currentSentence.text}-${chunkIndex}`;
        setShadowingState(prev => ({
          ...prev,
          processedSentences: new Set(Array.from(prev.processedSentences).concat([sentenceKey]))
        }));
      }
    }
    
    setDialogState(prev => ({ ...prev, showShadowingDialog: false }));
    setShadowingState(prev => ({ ...prev, showTranslation: false, showSentence: false }));

    // 清理任何剩余的timeout
    if (shadowingTimeoutRef.current) {
      clearTimeout(shadowingTimeoutRef.current);
      shadowingTimeoutRef.current = null;
    }
    
    // 清除所有replay相关标志
    isReplayingRef.current = false;
    shadowingReplayCompletedRef.current = false;
    
    // 增加延迟并等待seekTo完成
    setTimeout(async () => {
      // 优先使用全局播放器，回退到传入的player
      const activePlayer = globalPlayer || player;
      
      if (shadowingState.currentSentence && activePlayer) {
        const chunkEndTime = shadowingState.currentSentence.timestamp[1] + 0.1;
        // console.log(`🎯 Continue: 跳转到chunk结尾时间 ${chunkEndTime}，避开触发区域`);
        activePlayer.seekTo(chunkEndTime);
        
        // 等待seekTo操作完成
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      syncPlayerState('play', 'shadowing continue').catch(error => {
        console.error("Error resuming playback:", error);
      });
    }, 100); // 减少初始延迟，但增加seekTo后的等待
  }, [syncPlayerState, dialogState.currentEvent, shadowingState.currentSentence, getStateMachine, globalPlayer, player]);

  // ===== chunk点击处理 =====
  const handleChunkClick = useCallback(
    (index: number, chunk: any, event: React.MouseEvent<HTMLSpanElement>) => {
      // 检查点击是否来自高亮的单词，如果是则不处理chunk点击
      const target = event.target as Element;
      const isWordElement = Boolean(target.closest('[data-word-highlight-element]'));

      if (!isMobile && isWordElement) {
        return;
      }
      
      if (isMobile) {
        const now = Date.now();
        const timeDiff = now - uiState.lastTapTime;
        const isDoubleTap = timeDiff < 300;

        if (isDoubleTap) {
          if (tapTimerRef.current) {
            clearTimeout(tapTimerRef.current);
            tapTimerRef.current = null;
          }

          // Get the position of the clicked element
          const rect = event.currentTarget.getBoundingClientRect();
          const position = {
            x: rect.left,
            y: rect.bottom // 使用视口坐标，因为FloatingToolbar用的是fixed定位
          };

          setActiveChunk(index);
          setShowButton(false);
          setToolbarPosition(position);
          setShowToolbar(true);
          
          // 新增：双击时高亮chunk中的单词
          setHighlightedChunkIndex(index);
          return;
        }

        setLastTapTime(now);

        // 单击单词时交给 WordHoverCard 处理
        if (isWordElement) {
          return;
        }

        tapTimerRef.current = window.setTimeout(() => {
          performSeek();
          tapTimerRef.current = null;
        }, 250);
      } else {
        performSeek();
      }

      function performSeek() {
        // 优先使用全局播放器
        if (globalPlayer && globalPlayer.seekTo) {
          console.log('🎯 Chunk click - using global player to seek to:', chunk.timestamp[0]);
          try {
            if (typeof globalPlayer.beginScrubbing === "function") {
              globalPlayer.beginScrubbing();
            }
            if (typeof globalPlayer.scrubTo === "function") {
              globalPlayer.scrubTo(chunk.timestamp[0]);
            } else {
              globalPlayer.seekTo(chunk.timestamp[0]);
            }
          } finally {
            if (typeof globalPlayer.endScrubbing === "function") {
              setTimeout(() => {
                try {
                  globalPlayer.endScrubbing?.();
                } catch (error) {
                  console.error("Error ending scrub:", error);
                }
              }, 0);
            }
          }
          try {
            if (globalPlayer.play) {
              globalPlayer.play();
            }
          } catch (error) {
            console.error("Error playing with global player:", error);
          }
          return;
        }
        
        // 回退到本地播放器
        if (player) {
          console.log('🎯 Chunk click - using local player to seek to:', chunk.timestamp[0]);
          player.seekTo(chunk.timestamp[0]);
          try {
            player.play();
          } catch (error) {
            console.error("Error playing video:", error);
          }
          return;
        }
        
        // 最后回退到onSeek回调
        if (onSeek) {
          console.log('🎯 Chunk click - using onSeek callback to seek to:', chunk.timestamp[0]);
          onSeek(chunk.timestamp[0]);
        }
      }
    },
    [
      isMobile,
      uiState.lastTapTime,
      setLastTapTime,
      setActiveChunk,
      setShowButton,
      setShowToolbar,
      setToolbarPosition,
      globalPlayer,
      player,
      onSeek,
      tapTimerRef,
    ]
  );

  // ===== 不再需要初始化逻辑，事件系统自动管理状态 =====


  // ===== 自动滚动效果 =====
  useEffect(() => {
    if (isActive && entryRef.current) {
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
      }

      scrollTimerRef.current = setTimeout(() => {
        if (entryRef.current) {
          const rect = entryRef.current.getBoundingClientRect();
          const isInView =
            rect.top >= 0 && rect.top <= window.innerHeight * 0.3;

          if (!isInView) {
            const elementTop = entryRef.current.getBoundingClientRect().top;
            const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
            const offsetBase = viewportWidth < 768
              ? 344
              : viewportWidth < 1280
                ? (isVideoMinimized ? 180 : 550)
                : (isVideoMinimized ? 120 : 365);
            const offsetTop = window.pageYOffset + elementTop - offsetBase;

            window.scrollTo({
              top: offsetTop,
              behavior: "smooth",
            });
          }
        }
        scrollTimerRef.current = null;
      }, 150);

      return () => {
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = null;
        }
      };
    }
  }, [isActive, isVideoMinimized]);

  // ===== 词汇卡引用效果 =====
  useEffect(() => {
    if (dialogState.savedVocabWords.length > 0) {
      vocabCardRefs.current = dialogState.savedVocabWords.map(() => null);

      dialogState.savedVocabWords.forEach((word) => {
        if (!explanations[word.original]) {
          getExplanation(word.original);
        }
      });
    }
  }, [dialogState.savedVocabWords, explanations, getExplanation]);

  // ===== 键盘事件处理 =====
  useEffect(() => {
    if (dialogState.showVocabDialog || dialogState.showShadowingDialog) {
      const handleKeyDown = async (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (dialogState.showVocabDialog && continueButtonRef.current) {
            continueButtonRef.current.click();
          } else if (dialogState.showShadowingDialog && !isRecording) {
            handleShadowingContinue();
          }
        } else if ((e.key === "r" || e.key === "R") && dialogState.showShadowingDialog && !isRecording) {
          e.preventDefault();
          handleShadowingReplay();
        } else if (e.key === " " && dialogState.showShadowingDialog && !isTranscribing) {
          // 空格键控制录音
          e.preventDefault();
          if (isRecording) {
            stopRecording();
            setShadowingState(prev => ({ ...prev, showTranscription: true }));
          } else {
            // 记录录音开始时间
            recordingStartTimeRef.current = Date.now();
            setShadowingState(prev => ({
              ...prev,
              showTranscription: false,
              transcriptionAccuracy: null
            }));
            await startRecording();
          }
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [
    dialogState.showVocabDialog,
    dialogState.showShadowingDialog,
    handleShadowingContinue,
    handleShadowingReplay,
    isRecording,
    isTranscribing,
    startRecording,
    stopRecording,
  ]);

  // ===== 进度清理事件监听 =====
  useEffect(() => {
    const handleClearProgress = () => {
      setDialogState({
        showVocabDialog: false,
        showShadowingDialog: false,
        currentEvent: null,
        savedVocabWords: [],
        completedWords: {},
        allWordsCompleted: false,
        activeWordIndex: 0,
        showQuizTranslation: false,
        revealedHiddenWords: {},
      });

      setShadowingState({
        currentSentence: null,
        processedSentences: new Set(),
        showTranslation: false,
        showSentence: false,
        playbackSpeed: 1.0,
        showTranscription: false,
        transcriptionAccuracy: null,
      });
    };

    const handleEmergencyCleanup = () => {
      handleClearProgress();
    };

    window.addEventListener("clearVocabularyProgress", handleClearProgress as EventListener);
    window.addEventListener("emergencyMemoryCleanup", handleEmergencyCleanup as EventListener);

    return () => {
      window.removeEventListener("clearVocabularyProgress", handleClearProgress as EventListener);
      window.removeEventListener("emergencyMemoryCleanup", handleEmergencyCleanup as EventListener);
    };
  }, []);


  // ===== 组件卸载清理 =====
  useEffect(() => {
    return () => {
      if (shadowingTimeoutRef.current) {
        clearTimeout(shadowingTimeoutRef.current);
        shadowingTimeoutRef.current = null;
      }
      if (vocabularyReplayTimeoutRef.current) {
        clearTimeout(vocabularyReplayTimeoutRef.current);
        vocabularyReplayTimeoutRef.current = null;
      }
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
      cleanup();
    };
  }, [cleanup]);

  // ===== 沉浸模式早期返回 =====
  if (isImmersiveMode) {
    return null;
  }

  return (
    <div
      ref={entryRef}
      data-active={isActive}
      className={`px-2 md:px-3 sm:px-8 pt-0 sm:pt-1 pb-0 sm:pb-2 sm:py-3 relative ${
        isVideoMinimized ? "scroll-mt-[100px]" : "scroll-mt-[345px]"
      }`}
    >
      {/* 时间戳和跳转按钮 */}
      <div className="flex items-center mb-0 sm:mb-2">
        <div className={cn("w-1 h-4 sm:h-6 mr-0 sm:mr-3 rounded-full shadow-sm", isActive ? "bg-red-500" : "bg-gradient-to-b from-primary via-primary/80 to-primary/60")}></div>
        <Button
          variant="ghost"
          className="group flex items-center space-x-2 px-3 py-0 sm:py-1.5 h-auto text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all duration-200 bg-transparent border border-transparent hover:border-primary/20 rounded-lg font-medium text-sm"
          onClick={() => jumpToTime(group.timestamp[0])}
        >
          <span className="font-mono text-[14px] tracking-wide">
            {formatTime(group.timestamp[0])}
          </span>
        </Button>
      </div>

      {/* 英文文本内容 */}
      <div
        className="font-oldschool-grotesk-book text-[1.15rem] md:text-[1.3rem] sm:text-[1.3rem] leading-[1.45] break-words"
      >
        {group.chunks?.map((chunk, index) => {
          const isCurrentChunk = index === currentChunkIndex;
          const isActiveUIChunk = index === uiState.activeChunk;
          const isHoveredChunk = uiState.hoveredChunk === index;

          return (
            <span
              key={`${index}-${practiceMode ? 'practice' : 'normal'}-${practiceModeType}-${isWordCheckerReady ? 1 : 0}`}
              ref={isActiveUIChunk ? activeSpanRef : null}
              className={`cursor-pointer relative before:content-[' '] before:absolute before:inset-0 before:rounded-sm before:-z-10 ${
                isCurrentChunk
                  ? "bg-red-500 text-white rounded-md px-0.5 pb-0.5"
                : isActiveUIChunk
                ? "rounded-sm px-0.5 pb-0.5"
                : isMobile
                ? "rounded-sm px-0.5 pb-0.5 select-none"
                : "hover:text-[#000000] hover:bg-red-500/20 rounded-sm px-0.5 pb-0.5"
              } whitespace-normal`}
              style={
                isMobile
                  ? {
                      // WebkitTouchCallout: "none",
                      // WebkitUserSelect: "none",
                      // userSelect: "none",
                    }
                  : undefined
              }
              onClick={(e) => handleChunkClick(index, chunk, e)}
              onMouseEnter={
                isMobile
                  ? undefined
                  : () => !uiState.showToolbar && setHoveredChunk(index)
              }
              onMouseLeave={
                isMobile
                  ? undefined
                  : () => !uiState.showToolbar && setHoveredChunk(null)
              }
              onContextMenu={
                isMobile
                  ? (e: React.MouseEvent<HTMLSpanElement>) => {
                      e.preventDefault();
                    }
                  : undefined
              }
            >
              {/* 渲染chunk文本 */}
              {(() => {
                const shouldHighlight = uiState.activeChunk === index && uiState.showToolbar;
                const normalizedChunkText = chunk.text.replace(/\u00a0/g, " ");
                
                if (shouldHighlight) {
                  const tokens = normalizedChunkText.match(/\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b|\d+%?|\s+|[^\w\s]/g);
                  return (
                    <span data-word-highlight style={{ pointerEvents: "auto" }}>
                      {tokens?.map((token, tokenIndex) => {
                    const isValidWord = /^[a-zA-Z]+(?:'[a-zA-Z]+)?$/.test(token);
                    
                    if (!isValidWord) {
                      return <span key={tokenIndex}>{token}</span>;
                    }
                    
                    // 移动端和桌面端都使用 WordHoverCard 组件
                    return (
                      <WordHoverCard
                        key={tokenIndex}
                        word={token}
                        index={tokenIndex}
                        isSearchMatch={false} // chunk高亮模式使用有道翻译
                        isWordCheckerReady={isWordCheckerReady}
                        userSettings={userSettings}
                        explanations={explanations}
                        loading={loading}
                        savedWords={savedWords}
                        savingWord={savingWord}
                        onGetExplanation={getExplanation}
                        onSaveCard={saveCard}
                        isCardSaved={isCardSaved}
                        selectedWord={uiState.selectedWord}
                        showWordDrawer={uiState.showWordDrawer}
                        setSelectedWord={setSelectedWord}
                        setShowWordDrawer={setShowWordDrawer}
                        speakerRef={speakerRef}
                        // 添加有道翻译支持
                        youdaoTranslations={translations}
                        youdaoLoading={youdaoLoading}
                        youdaoGetTranslation={getTranslation}
                        youdaoIsInitialized={isInitialized}
                        // 传入保存所需的上下文信息
                        episodeId={episodeId}
                        episodeTitle={episodeTitle}
                        episodeThumbnail={episodeThumbnail}
                        contextText={normalizedChunkText}
                        variant="subtitle"
                        isHighlighted
                        enableMobileDrawer={false}
                      />
                    );
                  })}
                </span>
              );
                } else {
                  // 🎯 使用带 vocabulary 高亮的渲染函数
                  return renderTextWithVocabHighlight(normalizedChunkText, index);
                }
              })()}

              {!isMobile && isHoveredChunk && uiState.showButton && (
                <button
                  data-floating-button
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-[calc(100%-12px)] bg-background backdrop-blur-sm rounded-full p-1.5 shadow-sm border-2 border-foreground transition-all z-20"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    // 设置高亮chunk中的单词
                    setHighlightedChunkIndex(index);
                    
                    // 设置工具栏状态
                    const rect = e.currentTarget.parentElement?.getBoundingClientRect();
                    if (rect) {
                      const position = {
                        x: rect.left,
                        y: rect.bottom
                      };
                      setActiveChunk(index);
                      setShowButton(false);
                      setToolbarPosition(position);
                      setShowToolbar(true);
                    }
                  }}
                >
                  <WandSparkles className="w-5 h-5 text-foreground text-semibold stroke-[2]" />
                </button>
              )}


              {uiState.showAnalysis && isActiveUIChunk && !isMobile && (
                <StructuredAnalysisDialog
                  isVisible={uiState.showAnalysis}
                  isAnalyzing={uiState.isAnalyzing}
                  analysisObject={analysisObject}
                  onClose={handleCloseAnalysis}
                  triggerElementRef={activeSpanRef}
                />
              )}
            </span>
          );
        })}
      </div>

      {/* 翻译部分 */}
      {showTranslation && getTranslationText(group.translation) && (
        <div
          className="text-[0.90rem] sm:text-[1.05rem] leading-[1.5] text-muted-foreground/80 mt-1 sm:mt-3 pl-4 border-l-3 border-primary/30 bg-muted/30 rounded-xl py-2 pr-3"
        >
          <span>
            {getTranslationText(group.translation)}
          </span>
        </div>
      )}

      {showTranslation &&
        !getTranslationText(group.translation) &&
        group.chunks?.some((chunk) => getTranslationText(chunk.translation)) && (
          <div
            className="mt-3 pl-4 border-l-3 border-primary/30 bg-muted/30 rounded-r-lg py-2 pr-3"
          >
            {group.chunks?.map((chunk, index) => {
              const activeChunk = group.chunks?.[currentChunkIndex];
              const chunkTranslation = getTranslationText(chunk.translation);
              return chunkTranslation ? (
                <span
                  key={`translation-${index}`}
                  className={`text-[1.05rem] sm:text-[1.15rem] leading-[1.5] text-muted-foreground/80 ${
                    chunk === activeChunk
                      ? "bg-primary/10 rounded-md px-2 py-1 border border-primary/20"
                      : ""
                  }`}
                >
                  {chunkTranslation}
                  {index < (group.chunks?.length || 0) - 1 && " "}
                </span>
              ) : null;
            })}
          </div>
        )}

      {showTranslation &&
        !getTranslationText(group.translation) &&
        (!group.chunks ||
          !group.chunks.some((chunk) => getTranslationText(chunk.translation))) && (
          <div
            className="mt-3 pl-4 border-l-3 border-muted-foreground/20 bg-muted/20 rounded-r-lg py-2 pr-3"
          >
            <div
              className="text-sm text-gray-400 italic"
            >
              {t('vocabQuiz.translationUnavailable', 'Translation not available for this segment')}
            </div>
          </div>
        )}

      {/* 词汇对话框 */}
      <Dialog
        open={dialogState.showVocabDialog}
        onOpenChange={handleVocabDialogOpenChange}
      >
        <DialogContent 
          className="sm:max-w-2xl rounded-[40px] border border-border/70 bg-popover/95 shadow-[0_30px_60px_-25px_rgba(15,23,42,0.35)] backdrop-blur supports-[backdrop-filter]:bg-popover/75"
          onPointerDownOutside={(e) => {
            e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            e.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-center font-sans sm:text-2xl text-xl font-semibold pb-0">
              {t('vocabQuiz.title', 'Vocabulary Quiz')}
              {vocabularyDisplayName && (
                <span className="ml-2 text-base font-semibold text-muted-foreground sm:text-lg">
                  {vocabularyDisplayName}
                </span>
              )}
            </DialogTitle>
            {/* <DialogDescription className="text-center text-gray-500">
              Practice the vocabulary words found in this sentence
            </DialogDescription> */}
          </DialogHeader>

          {/* 句子内容区域 - 移出DialogDescription避免HTML嵌套问题 */}
          <div className="mb-5 rounded-3xl border border-border/60 bg-popover/85 px-4 py-5 sm:px-6 sm:py-6 text-muted-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.4)] backdrop-blur supports-[backdrop-filter]:bg-popover/65">
            {/* 显示上下文：前一句话（如果存在）*/}
            {dialogState.currentEvent && dialogState.currentEvent.chunkIndex > 0 && group.chunks && (
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground/70 mb-4 text-center">
                &ldquo;{group.chunks[dialogState.currentEvent.chunkIndex - 1]?.text}&rdquo;
              </div>
            )}

            <div className="flex items-start justify-between gap-3">
              {/* 显示包含隐藏单词的当前句子 */}
              <div className="flex-1 text-left text-foreground text-lg md:text-xl leading-relaxed font-medium">
                {dialogState.currentEvent?.payload?.chunkText ? (
                  renderSentenceWithHiddenWords(
                    dialogState.currentEvent.payload.chunkText, 
                    dialogState.savedVocabWords
                  )
                ) : (
                  <div className="text-muted-foreground italic">{t('vocabQuiz.loading', 'Loading...')}</div>
                )}
              </div>

              {hasQuizTranslation && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 rounded-full border border-primary/30 shadow-sm backdrop-blur-sm transition-all hover:bg-primary/10 hover:border-primary/50 focus-visible:ring-primary/40"
                  onClick={handleToggleQuizTranslation}
                  aria-expanded={dialogState.showQuizTranslation}
                  aria-label={
                    dialogState.showQuizTranslation
                      ? t('vocabQuiz.translation.hide', 'Hide translation')
                      : t('vocabQuiz.translation.show', 'Show translation')
                  }
                  title={
                    dialogState.showQuizTranslation
                      ? t('vocabQuiz.translation.hide', 'Hide translation')
                      : t('vocabQuiz.translation.show', 'Show translation')
                  }
                >
                  <Languages className="h-4 w-4" />
                  <span className="sr-only">
                    {dialogState.showQuizTranslation
                      ? t('vocabQuiz.translation.hide', 'Hide translation')
                      : t('vocabQuiz.translation.show', 'Show translation')}
                  </span>
                </Button>
              )}
            </div>

            {dialogState.showQuizTranslation && hasQuizTranslation && (
              <div className="mt-4 rounded-2xl bg-white/60 px-4 py-3 text-sm text-muted-foreground shadow-sm backdrop-blur-sm supports-[backdrop-filter]:bg-white/45">
                {currentQuizTranslation}
              </div>
            )}
          </div>

          <div className="py-2 max-h-[60vh] overflow-y-auto">
            <div className="grid gap-4">
              {dialogState.savedVocabWords.map((word, index) => (
                <VocabCard
                  key={`${word.original}-${index}`}
                  ref={(el) => {
                    vocabCardRefs.current[index] = el;
                  }}
                  word={word.original}
                  pronunciation={
                    explanations[word.original]?.usphone || undefined
                  }
                  translation={getWordTranslation(word)}
                  isInitiallyCompleted={dialogState.completedWords[word.original] || false}
                  autoFocus={index === dialogState.activeWordIndex}
                  languageSetting={userSettings?.languageSetting}
                  onCorrect={() => {
                    handleVocabWordCompleted(word.original);

                    if (index < dialogState.savedVocabWords.length - 1) {
                      setDialogState(prev => ({
                        ...prev,
                        activeWordIndex: index + 1
                      }));
                      setTimeout(() => {
                        const nextCard = vocabCardRefs.current[index + 1];
                        if (nextCard) {
                          nextCard.focus();
                        }
                      }, 200);
                    }
                  }}
                />
              ))}
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-center gap-2 !flex-nowrap">
            <Button type="button" variant="outline" className="font-light" onClick={handleReplay}>
              {t('vocabQuiz.actions.replay', 'Replay')}
            </Button>
            <Button
              type="button"
              ref={continueButtonRef}
              className="font-light"
              onClick={handleContinuePlayback}
            >
              {t('vocabQuiz.actions.continue', 'Continue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 阴影练习对话框 */}
      <Dialog
        open={dialogState.showShadowingDialog}
        onOpenChange={handleShadowingDialogOpenChange}
      >
        <DialogContent className="sm:max-w-2xl rounded-[30px] px-2 sm:py-6">
          <DialogHeader>
            <DialogTitle className="text-center text-xl sm:text-2xl font-bold text-gray-700 pb-0">
              {t('shadowing.title', 'Shadowing Practice')}
            </DialogTitle>
            {/* <DialogDescription className="text-center text-gray-500 hidden sm:block">
              Listen and repeat the sentence to practice pronunciation and fluency
            </DialogDescription> */}
          </DialogHeader>

          {/* 句子内容区域 */}
          <div className="text-center text-gray-500 border-dashed border border-gray-300 p-2 rounded-xl">
            <div className="flex items-center justify-center gap-1 sm:gap-3 mb-3">
              {shadowingState.showSentence ? (
                <div className="flex-1 text-lg font-medium leading-tight text-left">
                  {/* 渲染可点击的单词 */}
                  {(shadowingState.currentSentence?.text || "").split(/(\s+|[^\w\s])/).map((token: string, index: number) => {
                    // 检查是否为单词（字母数字组合）
                    const isWord = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/.test(token);

                    if (isWord) {
                      return (
                        <span
                          key={index}
                          className="cursor-pointer text-primary hover:text-primary/80 hover:bg-primary/10 px-1 py-0.5 rounded transition-colors"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              // 使用TTS直接发音单个单词
                              const { doSpeak } = await import('@/components/tts');
                              const controller = new AbortController();

                              await doSpeak({
                                provider: 'EdgeTTS',
                                text: token.trim(),
                                lang: 'en',
                                rate: 10,
                                volume: 100,
                                signal: controller.signal,
                                onStartSpeaking: () => {
                                  console.log(`Started speaking: ${token.trim()}`);
                                },
                                onFinish: () => {
                                  console.log(`Finished speaking: ${token.trim()}`);
                                }
                              });
                            } catch (error) {
                              console.error('Failed to speak word:', token.trim(), error);
                            }
                          }}
                          title={`Click to pronounce: ${token}`}
                        >
                          {token}
                        </span>
                      );
                    } else {
                      // 非单词字符（空格、标点等）直接显示
                      return <span key={index}>{token}</span>;
                    }
                  })}
                </div>
              ) : (
                <div className="flex-1 text-md font-light text-gray-400 italic text-center">
                  {t('shadowing.showSentenceHint', 'Click the eye icon to show sentence')}
                </div>
              )}
              <div className="ml-auto flex flex-col items-end gap-2">
                <div className="cursor-pointer p-1.5 hover:bg-primary/10 rounded-lg transition-colors flex-shrink-0">
                  <SpeakerIcon
                    lang="en"
                    text={shadowingState.currentSentence?.text || ""}
                  />
                </div>
                <button
                  onClick={() => setShadowingState(prev => ({ ...prev, showSentence: !prev.showSentence }))}
                  className="cursor-pointer p-1.5 hover:bg-primary/10 rounded-lg transition-colors flex-shrink-0"
                >
                  {shadowingState.showSentence ? (
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {getTranslationText(shadowingState.currentSentence?.translation) && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                {shadowingState.showTranslation ? (
                  <div className="text-base text-gray-600">
                    {getTranslationText(shadowingState.currentSentence.translation)}
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShadowingState(prev => ({ ...prev, showTranslation: true }))}
                    className="text-primary/40 hover:text-primary hover:bg-primary/10"
                  >
                    {t('shadowing.showTranslation', 'Show Translation')}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* 转录结果显示 - 使用新的差异高亮组件 */}
          {shadowingState.showTranscription && transcribedText && shadowingState.currentSentence?.text && (
            <div className="mt-4 w-full">
              <TextDiffHighlight
                originalText={shadowingState.currentSentence.text}
                userTranscription={transcribedText}
                accuracy={shadowingState.transcriptionAccuracy || 0}
              />
            </div>
          )}

          {/* 错误提示 */}
          {transcriptionError && (
            <div className="mt-4 w-full p-3 bg-red-50 rounded-lg">
              <p className="text-sm text-red-600">
                {t('shadowing.transcribeFailed', 'Failed to transcribe')}: {transcriptionError}
              </p>
            </div>
          )}


          <DialogFooter className="flex justify-center sm:justify-center gap-2 mt-4">
            <div className="flex items-center gap-2 mr-4">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleShadowingReplay(0.75)}
                className="h-8 px-3"
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                0.75x
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-3"
                  >
                    <CircleGauge className="h-4 w-4" />
                    {/* <ChevronDown className="h-3 w-3" /> */}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onClick={() => {
                      setShadowingState(prev => ({ ...prev, playbackSpeed: 0.5 }));
                      handleShadowingReplay(0.5);
                    }}
                    className="cursor-pointer"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    0.5x
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setShadowingState(prev => ({ ...prev, playbackSpeed: 1.0 }));
                      handleShadowingReplay(1.0);
                    }}
                    className="cursor-pointer"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    1.0x
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => {
                      setShadowingState(prev => ({ ...prev, playbackSpeed: 1.25 }));
                      handleShadowingReplay(1.25);
                    }}
                    className="cursor-pointer"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" />
                    1.25x
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex gap-2 items-center">
              <Button
                type="button"
                variant="outline"
                className="font-light h-8 sm:h-10"
                onClick={() => handleShadowingReplay()}
              >
                {t('shadowing.replay', 'Replay')}
              </Button>
              <Button type="button" className="font-light h-8 sm:h-10" onClick={handleShadowingContinue}>
                {t('shadowing.continue', 'Continue')}
              </Button>

              {/* 录音按钮 - Dictate Button 风格 */}
              <div className="relative ml-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={async () => {
                          if (isRecording) {
                            stopRecording();
                            setShadowingState(prev => ({ ...prev, showTranscription: true }));
                          } else {
                            // 记录录音开始时间
                            recordingStartTimeRef.current = Date.now();
                            // 重置转录状态
                            setShadowingState(prev => ({
                              ...prev,
                              showTranscription: false,
                              transcriptionAccuracy: null
                            }));
                            await startRecording();
                          }
                        }}
                        disabled={isTranscribing}
                        aria-label={isRecording ? t('shadowing.stopRecording', 'Stop Recording') : t('shadowing.startShadowing', 'Start Shadowing')}
                        aria-pressed={isRecording}
                        aria-busy={isTranscribing}
                  className={`
                    w-9 h-9 border p-1.5 rounded-full cursor-pointer
                    inline-flex items-center justify-center
                    focus:bg-black/[0.15] dark:focus:bg-white/[0.7]
                    focus:outline-none focus-visible:outline-2 focus-visible:outline-black/80 dark:focus-visible:outline-white/80 focus-visible:outline-offset-2
                    ${isTranscribing ? 'opacity-50 cursor-not-allowed' : ''}
                    ${transcriptionError ? 'bg-red-100 hover:bg-red-200' : ''}
                  `}
                  style={{
                    boxSizing: 'border-box',
                    transition: isRecording ? 'background-color 0.2s ease' : 'box-shadow 0.05s linear, background-color 0.2s ease',
                    boxShadow: isRecording
                      ? `0 0 0 ${Math.max(0, Math.min(4, audioLevel * 6))}px rgba(0, 0, 0, ${Math.max(0.05, Math.min(0.4, audioLevel * 0.4))})`
                      : '0 0 0 0px rgba(0, 0, 0, 0)',
                  }}
                >
                  {/* Idle state - Microphone icon */}
                  {!isRecording && !isTranscribing && !transcriptionError && (
                    <svg
                      className="w-full h-full text-black/80 dark:text-black"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z"
                      />
                    </svg>
                  )}

                  {/* Recording state - Filled circle */}
                  {isRecording && (
                    <svg
                      className="w-full h-full text-red-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" />
                    </svg>
                  )}

                  {/* Processing state - Rotating spinner */}
                  {isTranscribing && (
                    <svg
                      className="w-full h-full text-black/80 dark:text-black animate-spin"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 2v4" />
                      <path d="m16.2 7.8 2.9-2.9" />
                      <path d="M18 12h4" />
                      <path d="m16.2 16.2 2.9 2.9" />
                      <path d="M12 18v4" />
                      <path d="m4.9 19.1 2.9-2.9" />
                      <path d="M2 12h4" />
                      <path d="m4.9 4.9 2.9 2.9" />
                    </svg>
                  )}

                  {/* Error state - Exclamation */}
                  {transcriptionError && !isRecording && !isTranscribing && (
                    <svg
                      className="w-full h-full text-red-600"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <line x1="12" x2="12" y1="4" y2="14" />
                      <line x1="12" x2="12.01" y1="20" y2="20" />
                    </svg>
                  )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{isRecording ? t('shadowing.stopRecording', 'Stop Recording') : t('shadowing.startShadowing', 'Start Shadowing')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {uiState.showToolbar && uiState.activeChunk !== null && (
        <div className="fixed top-full left-0 mt-2 z-[9999] pointer-events-auto" data-floating-toolbar>
          <FloatingToolbar
            triggerPosition={uiState.toolbarPosition || undefined}
            selectedText={group.chunks?.[uiState.activeChunk || 0]?.text || ''}
            onClose={() => {
              setShowToolbar(false);
              setActiveChunk(null);
              setHoveredChunk(null);
              setShowButton(true);
            }}
            onAnalyze={() => {
              const selectedChunk = group.chunks?.[uiState.activeChunk || 0];
              if (selectedChunk) {
                handleAnalyze(selectedChunk.text);
              }
            }}
            onPlaySlower={(speed: number) => {
              const selectedChunk = group.chunks?.[uiState.activeChunk || 0];
              const activePlayer = globalPlayer || player;
              
              if (selectedChunk && activePlayer?.seekTo) {
                try {
                  console.log('Seeking to:', selectedChunk.timestamp[0]);
                  activePlayer.seekTo(selectedChunk.timestamp[0]);
                  
                  // 设置播放速度
                  if (activePlayer === globalPlayer && activePlayer.setPlaybackSpeed) {
                    activePlayer.setPlaybackSpeed(speed);
                  } else if (activePlayer.setPlaybackRate) {
                    activePlayer.setPlaybackRate(speed);
                  }
                  if (activePlayer.play) {
                    // console.log('Starting playback');
                    activePlayer.play();
                  }
                  
                  // 监控播放进度，到达chunk结束时暂停并恢复1倍速
                  const intervalId = setInterval(() => {
                    if (activePlayer.getCurrentTime && activePlayer.getCurrentTime() >= selectedChunk.timestamp[1]) {
                      // console.log('Chunk finished, pausing and resetting speed');
                      clearInterval(intervalId);
                      if (activePlayer.pause) {
                        activePlayer.pause();
                      }
                      // 恢复播放速度
                      if (activePlayer === globalPlayer && activePlayer.setPlaybackSpeed) {
                        activePlayer.setPlaybackSpeed(1);
                      } else if (activePlayer.setPlaybackRate) {
                        activePlayer.setPlaybackRate(1);
                      }
                    }
                  }, 100);
                  
                } catch (error) {
                  console.error('Error in onPlaySlower:', error);
                }
              } else {
                console.warn('Missing selectedChunk or player.seekTo');
              }
            }}
            onPlaySpeed={(speed: number) => {
              const selectedChunk = group.chunks?.[uiState.activeChunk || 0];
              const activePlayer = globalPlayer || player;
              
              if (selectedChunk && activePlayer?.seekTo) {
                try {
                  activePlayer.seekTo(selectedChunk.timestamp[0]);
                  
                  // 设置播放速度
                  if (activePlayer === globalPlayer && activePlayer.setPlaybackSpeed) {
                    activePlayer.setPlaybackSpeed(speed);
                  } else if (activePlayer.setPlaybackRate) {
                    activePlayer.setPlaybackRate(speed);
                  }
                  
                  if (activePlayer.play) {
                    activePlayer.play();
                  }
                  
                  // 监控播放进度，到达chunk结束时暂停并恢复1倍速
                  const intervalId = setInterval(() => {
                    if (activePlayer.getCurrentTime && activePlayer.getCurrentTime() >= selectedChunk.timestamp[1]) {
                      // console.log('Chunk finished, pausing and resetting speed');
                      clearInterval(intervalId);
                      if (activePlayer.pause) {
                        activePlayer.pause();
                      }
                      // 恢复播放速度
                      if (activePlayer === globalPlayer && activePlayer.setPlaybackSpeed) {
                        activePlayer.setPlaybackSpeed(1);
                      } else if (activePlayer.setPlaybackRate) {
                        activePlayer.setPlaybackRate(1);
                      }
                    }
                  }, 100);
                  
                } catch (error) {
                  console.error('Error in onPlaySpeed:', error);
                }
              }
            }}
            onTriggerShadowing={() => {
              // 手动触发当前chunk的Shadowing Practice - 任何模式下都可以使用
              const selectedChunk = group.chunks?.[uiState.activeChunk || 0];
              if (selectedChunk) {
                console.log('🎤 Manually triggering Shadowing Practice for chunk', uiState.activeChunk);

                // 直接使用现有的Shadowing对话框逻辑
                setShadowingState(prev => ({
                  ...prev,
                  currentSentence: {
                    text: selectedChunk.text,
                    translation: selectedChunk.translation,
                    timestamp: selectedChunk.timestamp
                  },
                  showSentence: false,
                  showTranscription: false,
                  transcriptionAccuracy: null,
                }));

                setDialogState(prev => ({ ...prev, showShadowingDialog: true }));

                // 暂停播放器
                requestSkipHistory();
                syncPlayerState('pause', 'manual shadowing dialog').catch(error => {
                  console.error('Failed to sync pause for manual shadowing:', error);
                });

                // 关闭FloatingToolbar
                setShowToolbar(false);
                setActiveChunk(null);
                setHoveredChunk(null);
                setShowButton(true);
              }
            }}
          />
        </div>
      )}
      
      {/* 移动端AI分析 Drawer */}
      <MobileAnalysisDrawer
        isOpen={isMobile && uiState.showAnalysis && uiState.activeChunk !== null}
        isAnalyzing={uiState.isAnalyzing}
        analysisObject={analysisObject}
        selectedText={group.chunks?.[uiState.activeChunk || 0]?.text || ''}
        onClose={handleCloseAnalysis}
      />
      
      {/* 高亮单词的 Drawer - 只在非chunk高亮模式时显示 */}
      {isMobile && uiState.selectedWord && !(uiState.activeChunk !== null && uiState.showToolbar) && (
        <Drawer 
          open={uiState.showWordDrawer} 
          onOpenChange={(open) => {
            setShowWordDrawer(open);
            if (!open) {
              // 当drawer关闭时，清除选中的单词
              setSelectedWord('');
            }
          }}
        >
          <DrawerContent className="rounded-t-3xl border border-border">
            <DrawerHeader>
              <DrawerTitle className="flex flex-col items-start space-y-1">
                {selectedLookupTags.length > 0 && (
                  <div className="flex flex-wrap gap-1 text-[0.7rem] font-semibold uppercase tracking-wide text-primary/80">
                    {selectedLookupTags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded bg-primary/10 px-1.5 py-0.5"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <span className="flex items-center gap-2">
                  <GraduationCap className="h-6 w-6" />
                  <span className="text-2xl font-bold">{uiState.selectedWord}</span>
                </span>
              </DrawerTitle>
              {/* 显示音标 - 优先显示有道翻译音标，其次显示原有音标 */}
              {(translations[uiState.selectedWord]?.usphone || translations[uiState.selectedWord]?.phonetic || explanations[uiState.selectedWord]?.usphone) && (
                <DrawerDescription className="text-base text-start pl-8 font-medium text-stone-700">
                  /{translations[uiState.selectedWord]?.usphone || translations[uiState.selectedWord]?.phonetic || explanations[uiState.selectedWord]?.usphone}/
                </DrawerDescription>
              )}
            </DrawerHeader>
            
            <div className="px-4 pb-4">
              {isExplanationLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">Loading...</span>
                </div>
              ) : hasExplanationContent ? (
                <div className="space-y-4">
                  {/* 翻译内容 - 优先显示有道翻译 */}
                  {(translations[selectedWord]?.translation || translations[selectedWord]?.explains?.[0] || explanations[selectedWord]?.translation) && (
                    <div className="flex items-start space-x-3">
                      <MessageCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div className="text-md text-foreground/60">
                        {translations[selectedWord] ? (
                          <div>{translations[selectedWord]?.translation || translations[selectedWord]?.explains?.[0]}</div>
                        ) : explanations[selectedWord]?.translation ? (
                          explanations[selectedWord].translation.split(';').map((part: string, index: number) => (
                            <div key={index} className={index > 0 ? "mt-2" : ""}>
                              {part.trim()}
                            </div>
                          ))
                        ) : null}
                      </div>
                    </div>
                  )}
                  
                  {/* 详细解释 - 仅有道翻译 */}
                  {selectedWord && translations[selectedWord]?.explains && (translations[selectedWord]?.explains?.length || 0) > 1 && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <div className="text-sm text-gray-600">
                        {selectedWord && translations[selectedWord]?.explains?.slice(1).map((explain, index) => (
                          <div key={index} className={index > 0 ? "mt-1" : ""}>
                            {explain}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasSelectedLookupMetadata && (
                    <div className="rounded-lg border border-border/60 bg-muted/40 p-3 text-sm text-foreground/70">
                      {selectedLookupMetadata?.definition && (
                        <p className="whitespace-pre-line text-foreground/80">
                          {selectedLookupMetadata.definition}
                        </p>
                      )}
                      {selectedLookupMetadata?.tag && (
                        <div className="mt-2 flex flex-wrap gap-2 text-xs">
                          <span className="rounded bg-secondary/20 px-2 py-0.5 text-secondary-foreground/80">
                            {selectedLookupMetadata.tag}
                          </span>
                        </div>
                      )}
                      {(typeof selectedLookupMetadata?.bnc === 'number' || typeof selectedLookupMetadata?.frq === 'number') && (
                        <div className="mt-2 text-xs text-muted-foreground space-x-3">
                          {typeof selectedLookupMetadata?.bnc === 'number' && (
                            <span>BNC词频排名: {selectedLookupMetadata.bnc}</span>
                          )}
                          {typeof selectedLookupMetadata?.frq === 'number' && (
                            <span>COCA词频排名: {selectedLookupMetadata.frq}</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* 例句 - 仅原有解释服务 */}
                  {selectedWord && !translations[selectedWord] && explanations[selectedWord]?.example && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <span className="text-base text-gray-600 italic">
                        {explanations[selectedWord]?.example}
                      </span>
                    </div>
                  )}
                </div>
              ) : shouldShowNoExplanation ? (
                <div className="text-center py-4 text-gray-500">
                  No explanation available
                </div>
              ) : null}
            </div>
            
            <DrawerFooter>
              <div className="flex w-full items-stretch gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="default"
                      className={cn(
                        'flex-1 justify-between rounded-xl border text-sm h-10',
                        statusStyles[selectedStatusOption.value]
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <StatusIconMobile className="h-4 w-4" />
                        {selectedStatusOption.label}
                      </span>
                      <span className={cn('h-2 w-2 rounded-full', statusAccentMap[selectedStatusOption.value])} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {statusOptions.map((option) => {
                      const OptionIcon = option.value === 'MASTERED' ? Check : option.value === 'FUZZY' ? Lightbulb : Sparkles;
                      const isSelected = selectedStatusValue === option.value;
                      return (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={(event) => {
                            event.preventDefault();
                            setSelectedStatusValue(option.value);
                          }}
                          className={cn('flex items-start gap-2', isSelected && 'bg-accent/50 text-foreground')}
                        >
                          <OptionIcon className={cn('h-4 w-4 mt-0.5', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                          <span className="flex flex-col text-sm">
                            <span className="font-medium flex items-center gap-1">
                              {option.label}
                              {isSelected && <Check className="h-3 w-3" />}
                            </span>
                            <span className="text-xs text-muted-foreground">{option.description}</span>
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="default"
                  size="default"
                  className="flex-1 gap-2 font-normal sm:font-medium rounded-xl text-sm h-10"
                  disabled={
                    !userSettings?.myVocabularySetting ||
                    savingWord[uiState.selectedWord || ''] ||
                    isCardSaved(uiState.selectedWord || '') ||
                    (!explanations[uiState.selectedWord || ''] && !translations[uiState.selectedWord || ''])
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    const word = uiState.selectedWord;
                    if (!word || savingWord[word] || isCardSaved(word)) {
                      return;
                    }

                    const explanation = explanations[word];
                    const youdaoData = translations[word];

                    if (!explanation && !youdaoData) {
                      return;
                    }

                    const options: any = {
                      episodeId,
                      episodeTitle,
                      episodeThumbnail,
                      contextText: group.text,
                      status: selectedStatusValue,
                    };

                    if (!explanation && youdaoData) {
                      options.youdaoTranslation = {
                        translation: youdaoData.translation,
                        phonetic: youdaoData.phonetic,
                        usphone: youdaoData.usphone,
                        explains: youdaoData.explains,
                      };
                    }

                      saveCard(word, options);
                  }}
                >
                  {savingWord[uiState.selectedWord || ''] ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isCardSaved(uiState.selectedWord || '') ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )}
                  <span>
                    {isCardSaved(uiState.selectedWord || '')
                      ? t('common.saved', 'Saved')
                      : t('common.save')}
                  </span>
                </Button>
                <Button
                  variant="ghost"
                  size="default"
                  className="flex-1 border font-normal sm:font-medium rounded-xl text-sm h-10"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (speakerRef?.current) {
                      speakerRef.current.click();
                    }
                  }}
                >
                  <SpeakerIcon
                    divRef={speakerRef}
                    size={10}
                    provider="EdgeTTS"
                    text={uiState.selectedWord || ""}
                    lang={getSpeakerLang(userSettings?.languageSetting || "english") as any}
                    voice={undefined}
                    rate={10}
                    volume={100}
                  />
                  <span className="ml-2">{t('common.speak')}</span>
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
    </div>
  );
}

// ===== 导出组件 =====
export const MemoizedGroupedTranscriptEntry = GroupedTranscriptEntry; 
